import { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { Button } from '../../Components/UI/button';
import { Input } from '../../Components/UI/input';
import { Textarea } from '../../Components/UI/textarea';
import { Label } from '../../Components/UI/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../../Components/UI/dialog';
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from '../../Components/UI/alert-dialog';
import { Select, SelectTrigger, SelectContent, SelectItem } from '../../Components/UI/select';
import { Plus, X, Check, IndianRupee, ArrowLeft, Trash2, Settings, Upload, FileText, SquareArrowOutUpRight } from 'lucide-react';
import { useSelector } from 'react-redux';
import { manageThreadAPI } from './ThreadsAPI';
import { toast } from 'sonner';
import { selectThreadFieldPref, getUsersArr, getThreadTypesArr, userIsAdmin, checkIfInqOnlyMode, getThreadTypeByTID, isSmartSellerMode } from '../../Store/Reducers';
import { format, addDays } from 'date-fns';
import OrderStagesDialog from '../Settings/OrderStagesDialog';
import { TimeInputWithDialog } from '../../Components/Common/TimePickerWatch';
import ThreadFields from './ThreadFields.jsx';
import ConnectionEntryDialog from '../Connections/ConnectionEntry';
import CustomSearchableDropdown from '../../Components/Common/CustomSearchableDD';
import MastersDialog from '../Settings/Masters';
import { capitalizeFirstLetter, currentUserHasPermission, getConnectionOptionsWithSearch, getDefaultTime, getMobileHeaderTitle, getThreadNameFromTypeID, getTomorrowDate, isLive, isPremium, playOrderSound } from '../../Utils/Utilities';
import { DatePicker } from '../../Components/Common/DatePicker';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../Components/UI/tooltip';
import { getThreadByID } from '../../Utils/Utilities';
import BusinessInput from '../../Components/Common/BusinessInput';
import { validateTaskDateTime, getNextHourTime } from '../../Utils/Utilities';
import { getAuthData } from '../../Utils/AuthUtils';
import { AlertBox } from "../../Components/Common/AlertBox";
import useIsMobile from '../../Hooks/useIsMobile';
import Policies from '../Settings/Policies/Policies';
import SharingOptions from '../../Components/Common/SharingOptions';
import { SpeechControls } from '../../Components/Common/SpeechControls';
import { useCurrency } from '../../Hooks/useCurrency';
import { useUserConnectionFilter } from '../../Hooks/useUserConnectionFilter';
import { useModalBackButton } from '../../Hooks/useModalBackButton';
import { useHasConnections } from '../../Hooks/useHasConnections';
import apiService from '../../Services/APIService';
import { manageProductAPI } from '../Settings/MasterDataAPI';
import { MultiSelect } from '../../Components/UI/multi-select';
import ThreadDetails from './ThreadDetails';
import ConnectionFieldsDialog from '../Connections/ConnectionFields';
import ThrDetails from './ThrDetails';
import ConnectionDetailsWrapper from '../Connections/ConnectionDetailsWrapper';

// Reusable Tailwind classes
const threads_Classes = {
    spacing: "space-y-1.5",
    input: "h-11 text-sm transition-all duration-200 bg-white sahi-input",
    buttonPrimary: "sahi-primary-button",
    buttonSecondary: "sahi-secondary-button",
    gridCols1: "grid-cols-1",
    gridCols2: "grid-cols-1 sm:grid-cols-2",
    gridCols3: "grid-cols-1 sm:grid-cols-3",
    gridCols2Stretch: "grid-cols-1 sm:grid-cols-2 items-stretch",
    flexCol: "flex flex-col",
    flexRow: "flex flex-row",
    gap4: "gap-4",
    mb4: "mb-4",
    iconSize: "h-4 w-4",
    iconSizeMd: "h-5 w-5",
    iconSizeLg: "h-6 w-6"
};

// Main component that handles thread creation and editing with dynamic form fields
const ThreadEntry = ({
    triggerButton = true,
    triggerText = "Add Thread",
    triggerClassName = "bg-sahi-primary-color text-white sahi-primary-button",
    threadTypeID = 1,
    open: externalOpen,
    onOpenChange: externalOnOpenChange,
    threadID = null,
    onThreadUpdated,
    preselectedConnID = null,
    preselectedContactPerson = null,
    fromConnectionDetail = false,
    onThreadDeleted = () => { },
    prefilledData = null, // New prop for prefilling data from inquiry conversion
    calledFrom = '',
    forceOpen = false,
    extOperation = "",
    isDisabled = false,
    allowOpenOrders = false
}) => {
    const authData = getAuthData();
    const isMobileLayout = useIsMobile();
    const hasConnections = useHasConnections();
    const canAddConnections = currentUserHasPermission('ConnEntry');
    // Consolidated state management
    const [dialogStates, setDialogStates] = useState({
        main: false,
        fields: false,
        connection: false,
        deleteAlert: false,
        threadTypeID: threadTypeID,
        masterDialog: false,
        bizProspectConfirm: false,
        threadDetails: false,
        modelDialog: false,
        modelSearchText: '',
        connectionDetails: false,
        createdConnID: null
    });

    const canCloseSubModal = () => {
        return !forceOpen;
    };

    // Dialog state management
    const isOpen = externalOpen !== undefined ? externalOpen : dialogStates.main;
    const setIsOpen = externalOnOpenChange || ((open) => setDialogStates(prev => ({ ...prev, main: open })));

    const isInqOnlyMode = useSelector(checkIfInqOnlyMode);

    useEffect(() => {
        if (isOpen && !hasConnections && !threadID && !preselectedConnID && !fromConnectionDetail) {
            setIsOpen(false);
            setDialogStates(prev => ({ ...prev, connection: true }));
        }
    }, [isOpen, hasConnections, threadID, preselectedConnID, fromConnectionDetail]);

    const canSeeMoreDetails = currentUserHasPermission('AgnConfig');
    const canDltThr = currentUserHasPermission('ThrActions');
    const canShowDltBtn = canDltThr === 1;
    const [emailConfigOpen, setEmailConfigOpen] = useState(false);
    const currencySymbol = useCurrency();
    const { data: activeData } = useSelector(state => state.activeData);

    const lastRefInfo = useMemo(() => {
        let lastThrNos = activeData?.LastThrNos;
        if (typeof lastThrNos === 'string') {
            try { lastThrNos = JSON.parse(lastThrNos); } catch (e) { lastThrNos = []; }
        }
        if (!lastThrNos || !Array.isArray(lastThrNos)) return { prefill: '', hint: '' };

        const entry = lastThrNos.find(item => item.TID === threadTypeID);
        if (!entry || !entry.No) return { prefill: '', hint: '' };

        const lastNo = entry.No.toString().trim();
        const isNumeric = /^\d+$/.test(lastNo);

        if (isNumeric) {
            return { prefill: (parseInt(lastNo, 10) + 1).toString(), hint: '' };
        } else {
            return { prefill: '', hint: `Last: ${lastNo}` };
        }
    }, [activeData?.LastThrNos, threadTypeID]);

    let dfltHideFields = {
        dueDT: threadTypeID === 2 || (!allowOpenOrders && isInqOnlyMode && threadID),
        sharingOpts: false,
        moreDtls: !canSeeMoreDetails
    }
    const [secondaryContacts, setSecondaryContacts] = useState([]);
    const [hideFields, setHideFields] = useState(dfltHideFields);
    const currThreadType = useSelector(getThreadTypeByTID(threadTypeID))
    useEffect(() => {
        if (calledFrom === 'THRDONE' || calledFrom === 'THRREP' || calledFrom === 'ORDDONE') {
            setHideFields(prev => ({
                dueDT: true,
                sharingOpts: true,
                moreDtls: true
            }))
        }
        else {
            setHideFields({
                dueDT: threadTypeID === 2 || (!allowOpenOrders && isInqOnlyMode && threadID),
                sharingOpts: false,
                moreDtls: !canSeeMoreDetails || (!allowOpenOrders && isInqOnlyMode && threadTypeID === 2)
            });
        }
    }, [calledFrom, isInqOnlyMode, threadTypeID, threadID, canSeeMoreDetails, allowOpenOrders]);

    const [formData, setFormData] = useState({
        file: null,
        originalFileName: "",
        stageID: null
    });
    const [uiStates, setUIStates] = useState({
        selectedFields: ['connID', 'product', 'dueDT', 'businessValue', 'notes', 'reference'],
        datePickerStates: {},
        isLoadingThread: false,
        fetchedThreadData: null,
        isInitialLoad: true,
        userHasInteractedWithPocField: false,
        connectionSearchText: '',
        masterSearchText: '',
        fileName: ""
    });

    // Redux selectors for field preferences
    const prefsEnabled = {
        contactPerson: useSelector(state => selectThreadFieldPref(state, 'POC', threadTypeID)),
        model: useSelector(state => selectThreadFieldPref(state, 'Item', threadTypeID)),
        quantity: useSelector(state => selectThreadFieldPref(state, 'Qty', threadTypeID)),
        pocUserID: useSelector(state => selectThreadFieldPref(state, 'AsgnTo', threadTypeID)),
        nextTask: useSelector(state => selectThreadFieldPref(state, 'NextTsk', threadTypeID)),
        sinceDT: useSelector(state => selectThreadFieldPref(state, 'Since', threadTypeID)),
        source: useSelector(state => selectThreadFieldPref(state, 'Source', threadTypeID)),
        reference: useSelector(state => selectThreadFieldPref(state, 'Ref', threadTypeID)),
        status: threadTypeID === 1 // Show status only for TID 1
    };

    // Master data from Redux store
    const masterData = {
        connections: useSelector(state => state.connectionData.data || []),
        products: useSelector(state => state.masterData.products || []),
        taskTypes: useSelector(state =>
            (state.masterData.recordTypes || []).map(taskType => taskType.Typ)
        ),
        sources: useSelector(state => state.masterData.sources || []),
        thrExtraFields: useSelector(state => state.masterData.thrExtraFields || []),
        users: useSelector(getUsersArr),
        threadTypes: useSelector(getThreadTypesArr),
        allowSecConns: useSelector(state => state.masterData.allowSecConns),
        threadStages: useSelector(state => state.masterData.threadStages || [])
    };

    // Filter users based on selected connection 
    const filteredUsers = useSelector(getUsersArr);

    const threadData = useSelector(state => state.threadData?.data || []);
    const [masterDialogType, setMasterDialogType] = useState(null);
    const [selectedField, setSelectedField] = useState("");
    const isPurchaseType = masterData.threadTypes.find(tt => tt.TID == threadTypeID)?.Flow == "Out" ? true : false;
    const [shareByWhatsApp, setShareByWhatsApp] = useState(false);
    const [saveAsCompletedSale, setSaveAsCompletedSale] = useState(false);
    const [orderStatusText, setOrderStatusText] = useState('');
    const [orderStagesDialogOpen, setOrderStagesDialogOpen] = useState(false);
    const [orderStagesInitialView, setOrderStagesInitialView] = useState('list');
    const agencyData = useSelector((state) => state.agencyData.data);
    const [selectedThreadID, setSelectedThreadID] = useState(null);
    const [selectedThreadForDetails, setSelectedThreadForDetails] = useState(null);
    const [isOrderStagesExpanded, setIsOrderStagesExpanded] = useState(false);

    // Order stage options for Sale/Order entry (TID 2)
    const rawOrderStages = useMemo(() => {
        if (threadTypeID !== 2) return [];
        const threadTypeStages = masterData.threadStages?.find(t => Number(t.TID) === 2)?.Stages || [];
        return threadTypeStages.map(stage => ({
            id: stage.SID,
            name: stage.S
        })).filter(stage => stage.name);
    }, [threadTypeID, masterData.threadStages, orderStagesDialogOpen]);

    // Construct stage list based on mode (Sales Entry vs Sales Edit)
    const isEditMode = Boolean(threadID);

    const isCompletedSaleStatus = useMemo(() => {
        if (threadTypeID !== 2) return false;
        const currentData = uiStates.fetchedThreadData || prefilledData;
        const statusID = Number(currentData?.statusID ?? currentData?.StatusID ?? currentData?.Sts ?? 0);
        return statusID === 2 || saveAsCompletedSale;
    }, [threadTypeID, uiStates.fetchedThreadData, prefilledData, saveAsCompletedSale]);

    const isEditingCompletedSale = isEditMode && threadTypeID === 2 && isCompletedSaleStatus;

    const { visibleOrderStages, otherOrderStages, selectedOtherStage, remainingOthersCount } = useMemo(() => {
        if (threadTypeID !== 2) {
            return { visibleOrderStages: [], otherOrderStages: [], selectedOtherStage: null, remainingOthersCount: 0 };
        }

        const openOrderStage = { id: 'pending', name: 'Pending', label: 'Pending Order' };
        const completedSaleStage = { id: 'completed', name: 'Completed', label: 'Completed Sale' };

        if (isEditingCompletedSale) {
            // When editing a completed sale: show Pending Order and Completed Sale chips
            return {
                visibleOrderStages: [openOrderStage, completedSaleStage],
                otherOrderStages: [],
                selectedOtherStage: null,
                remainingOthersCount: 0,
            };
        }

        if (!isEditMode && !isOrderStagesExpanded) {
            // During sales entry (unexpanded): only Pending Order and Completed Sale
            return {
                visibleOrderStages: [openOrderStage, completedSaleStage],
                otherOrderStages: [],
                selectedOtherStage: null,
                remainingOthersCount: 0,
            };
        }

        // Expanded entry mode OR Edit mode for open/pending order:
        // Filter out any raw stages named pending or completed
        const customStages = rawOrderStages.filter(s => {
            const lower = s.name.trim().toLowerCase();
            return lower !== 'pending' && lower !== 'completed';
        });

        const visibleCustom = customStages.slice(0, 4);
        const others = customStages.slice(4);

        // For open order edit or expanded entry: Pending Order -> custom stages -> Completed Sale
        const visible = [openOrderStage, ...visibleCustom, completedSaleStage];

        const currentText = (orderStatusText || '').trim().toLowerCase();
        const isCurrentInVisible = visible.some(s => s.name.toLowerCase() === currentText);
        const matchedOther = !isCurrentInVisible && currentText
            ? others.find(s => s.name.toLowerCase() === currentText)
            : null;

        const remainingCount = matchedOther ? others.length - 1 : others.length;

        return {
            visibleOrderStages: visible,
            otherOrderStages: others,
            selectedOtherStage: matchedOther,
            remainingOthersCount: remainingCount,
        };
    }, [threadTypeID, rawOrderStages, orderStatusText, isEditMode, isOrderStagesExpanded, isEditingCompletedSale]);

    const handleOrderStatusSelect = (stageName) => {
        if (isEditingCompletedSale) return;

        const targetName = stageName || '';
        const lower = targetName.trim().toLowerCase();

        // If in entry mode: expand when Open Order / Pending is clicked, collapse when Completed Sale is clicked
        if (!isEditMode) {
            if (lower === 'pending' || lower === 'open order' || lower === 'pending order') {
                setIsOrderStagesExpanded(true);
            } else if (lower === 'completed' || lower === 'completed sale') {
                setIsOrderStagesExpanded(false);
            }
        }

        setOrderStatusText(targetName);
        if (lower === 'completed' || lower === 'completed sale') {
            setSaveAsCompletedSale(true);
        } else {
            setSaveAsCompletedSale(false);
            const matchedStage = rawOrderStages.find(s => s.name.toLowerCase() === lower);
            if (matchedStage) {
                setFormData(prev => ({ ...prev, stageID: matchedStage.id }));
            }
        }
    };

    const handleOrderStatusInputChange = (value) => {
        if (isEditingCompletedSale) return;
        setOrderStatusText(value);
        const lower = (value || '').trim().toLowerCase();
        if (lower === 'completed' || lower === 'completed sale') {
            setSaveAsCompletedSale(true);
            if (!isEditMode) setIsOrderStagesExpanded(false);
        } else {
            setSaveAsCompletedSale(false);
            const matchedStage = rawOrderStages.find(s => s.name.toLowerCase() === lower);
            if (matchedStage) {
                setFormData(prev => ({ ...prev, stageID: matchedStage.id }));
            }
        }
    };

    const handleEmailShareChange = (checked) => {
        setFormData(prev => ({
            ...prev,
            shareByEmail: checked
        }));
    };

    // Function to handle settings icon click
    const handleSettingsClick = () => {
        setEmailConfigOpen(true);
    };

    // Auto-assign current user as POC when creating new threads
    useEffect(() => {
        const currentUserID = authData?.UserID ? authData.UserID.toString() : "";

        // Only run for new threads (not edit mode) and when we have a current user
        if (!threadID && currentUserID && isOpen) {
            setFormData(prev => {
                const isConvertingFromInquiry = !!(prefilledData && prefilledData.pocUserID);
                const targetPoc = isConvertingFromInquiry
                    ? (prefilledData.pocUserID ? prefilledData.pocUserID.toString() : currentUserID)
                    : currentUserID;

                if (uiStates.userHasInteractedWithPocField && prev.pocUserID && prev.pocUserID !== "0" && prev.pocUserID !== 0) {
                    return prev;
                }

                if (prev.pocUserID === targetPoc) {
                    return prev;
                }

                return { ...prev, pocUserID: targetPoc };
            });
        }
    }, [authData?.UserID, threadID, uiStates.userHasInteractedWithPocField, prefilledData?.pocUserID, isOpen]);


    // useModalBackButton(isOpen, setIsOpen)

    // Generate default form data with proper date formatting
    const getInitialFormData = () => {
        const getCurrentDateTime = () => format(new Date(), 'dd-MMM-yy HH:mm:ss');
        const getOneWeekAhead = () => format(addDays(new Date(), 7), 'dd-MMM-yy');
        const currentUserID = authData?.UserID ? authData.UserID.toString() : "";

        const defaultData = {
            connID: preselectedConnID || '',
            contactPerson: preselectedContactPerson || '',
            product: '',
            model: '',
            reference: threadID ? (prefilledData?.reference || '') : (prefilledData?.reference || lastRefInfo.prefill || ''),
            quantity: '',
            dueDT: (() => {
                if (!allowOpenOrders && isInqOnlyMode && threadTypeID === 2 && threadID) {
                    return '';
                }
                if (calledFrom == 'THRDONE' || calledFrom === 'THRREP' || calledFrom === 'ORDDONE' || calledFrom == 'INQDONE') {
                    return getCurrentDateTime();
                }
                return getOneWeekAhead();
            })(),
            businessValue: '',
            pocUserID: currentUserID,
            nextTaskDT: getTomorrowDate(),
            nextTaskTime: getDefaultTime(),
            nextTaskType: '',
            nextTaskNotes: '',
            notes: '',
            sinceDT: getCurrentDateTime(),
            source: '',
            shareByEmail: false,
            file: null,
            stageID: (() => {
                if (masterData.threadStages && masterData.threadStages.length > 0) {
                    const threadTypeStages = masterData.threadStages.find(t => t.TID === threadTypeID)?.Stages || [];
                    if (threadTypeStages.length > 0) {
                        return threadTypeStages[0].SID;
                    }
                }
                return null;
            })()
        };

        if (masterData.thrExtraFields && masterData.thrExtraFields.length > 0) {
            const currentThreadFields = masterData.thrExtraFields.find(tf => tf.TID === threadTypeID);
            if (currentThreadFields && currentThreadFields.Fields) {
                let fieldsArray = [];
                try {
                    fieldsArray = typeof currentThreadFields.Fields === 'string'
                        ? JSON.parse(currentThreadFields.Fields)
                        : currentThreadFields.Fields;
                } catch (error) {
                    console.error("Error parsing thread extra fields:", error);
                    fieldsArray = [];
                }

                if (Array.isArray(fieldsArray) && fieldsArray.length > 0) {
                    fieldsArray.forEach(field => {
                        const fieldName = field.F;
                        if (fieldName) {
                            const fieldId = `extraField_${field.FID}`;
                            defaultData[fieldId] = "";
                        }
                    });
                }
            }
        }

        if (uiStates.fetchedThreadData) {
            const editData = { ...defaultData, ...uiStates.fetchedThreadData };
            if (fromConnectionDetail && preselectedConnID) {
                editData.connID = preselectedConnID;
                editData.contactPerson = preselectedContactPerson || editData.contactPerson;
            }
            if (editData.pocUserID === 0 || editData.pocUserID === "0" || !editData.pocUserID) {
                editData.pocUserID = currentUserID;
            } else if (editData.pocUserID) {
                editData.pocUserID = editData.pocUserID.toString();
            }
            return editData;
        }

        // Apply prefilled data if provided (for inquiry conversion or edit fallback)
        if (prefilledData) {
            const normalizedPrefill = {
                ...prefilledData,
                connID: prefilledData.connID ?? prefilledData.ConnID ?? defaultData.connID,
                contactPerson: prefilledData.contactPerson ?? prefilledData.ContactPerson ?? prefilledData.ContactName ?? prefilledData.contactName ?? defaultData.contactPerson,
                product: prefilledData.product ?? prefilledData.MainProduct ?? prefilledData.mainProduct ?? prefilledData.Product ?? defaultData.product,
                model: prefilledData.model ?? prefilledData.Model ?? defaultData.model,
                reference: prefilledData.reference ?? prefilledData.Reference ?? (!threadID ? (lastRefInfo.prefill || defaultData.reference) : defaultData.reference) ?? '',
                quantity: prefilledData.quantity ?? prefilledData.mainQty ?? prefilledData.MainQty ?? prefilledData.Quantity ?? defaultData.quantity,
                businessValue: prefilledData.businessValue !== undefined && prefilledData.businessValue !== null && prefilledData.businessValue !== ''
                    ? prefilledData.businessValue
                    : (prefilledData.bizProspect !== undefined && prefilledData.bizProspect !== null && prefilledData.bizProspect !== ''
                        ? prefilledData.bizProspect
                        : (prefilledData.BizProspect !== undefined && prefilledData.BizProspect !== null && prefilledData.BizProspect !== ''
                            ? prefilledData.BizProspect
                            : defaultData.businessValue)),
                notes: prefilledData.notes ?? prefilledData.Notes ?? defaultData.notes,
                source: prefilledData.source ?? prefilledData.Source ?? defaultData.source,
                pocUserID: prefilledData.pocUserID ? prefilledData.pocUserID.toString() : (prefilledData.POCUserID ? prefilledData.POCUserID.toString() : defaultData.pocUserID),
                sinceDT: (() => {
                    const sDT = prefilledData.sinceDT ?? prefilledData.SinceDT;
                    if (!sDT) return defaultData.sinceDT;
                    try {
                        return format(new Date(sDT), 'dd-MMM-yy HH:mm:ss');
                    } catch (e) {
                        return sDT;
                    }
                })(),
                dueDT: (() => {
                    const dDT = prefilledData.dueDT ?? prefilledData.DueDT;
                    if (!dDT) return defaultData.dueDT;
                    try {
                        return format(new Date(dDT), 'dd-MMM-yy');
                    } catch (e) {
                        return dDT;
                    }
                })(),
                inquiryThreadID: prefilledData.inquiryThreadID ?? prefilledData.threadID ?? prefilledData.ThreadID ?? defaultData.inquiryThreadID
            };

            const merged = { ...defaultData, ...normalizedPrefill };
            if (!threadID && !merged.reference) {
                merged.reference = lastRefInfo.prefill || defaultData.reference || '';
            }
            return merged;
        }

        return defaultData;
    };

    useEffect(() => {
        // Only initialize form data when dialog opens or when fetchedThreadData changes
        if (isOpen && (uiStates.isInitialLoad || uiStates.fetchedThreadData !== null)) {
            const initialData = getInitialFormData();
            setFormData(initialData);

            // Mark as no longer initial load
            if (uiStates.isInitialLoad) {
                setUIStates(prev => ({ ...prev, isInitialLoad: false }));
            }

            // Ensure current user is set for POC in new threads
            const currentUserID = authData?.UserID ? authData.UserID.toString() : "";
            if (!threadID && currentUserID && (!initialData.pocUserID || initialData.pocUserID === "0")) {
                setFormData(prev => ({ ...prev, pocUserID: currentUserID }));
            }
        }

    }, [isOpen, uiStates.isInitialLoad, uiStates.fetchedThreadData, preselectedConnID, preselectedContactPerson, fromConnectionDetail, authData?.UserID, threadID, calledFrom]);

    // Propose reference number for new thread/sale entry when prefill becomes available
    useEffect(() => {
        if (isOpen && !threadID && (!formData.reference || formData.reference.trim() === '') && lastRefInfo.prefill) {
            setFormData(prev => ({
                ...prev,
                reference: lastRefInfo.prefill
            }));
        }
    }, [isOpen, threadID, lastRefInfo.prefill]);

    // Set default stage when thread stages load asynchronously
    useEffect(() => {
        if (isOpen && !threadID && !formData.stageID && masterData.threadStages && masterData.threadStages.length > 0) {
            const threadTypeStages = masterData.threadStages.find(t => t.TID === threadTypeID)?.Stages || [];
            if (threadTypeStages.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    stageID: threadTypeStages[0].SID
                }));
            }
        }
    }, [isOpen, threadID, masterData.threadStages, threadTypeID, formData.stageID]);

    useEffect(() => {
        if (!isOpen) {
            // Reset form when dialog closes
            setUIStates(prev => ({
                ...prev,
                isInitialLoad: true,
                fetchedThreadData: null,
                userHasInteractedWithPocField: false
            }));
            setOrderStatusText('');
            setIsOrderStagesExpanded(false);
        } else {
            if (calledFrom === 'ORDDONE' || calledFrom === 'THRDONE' || calledFrom === 'INQDONE') {
                setSaveAsCompletedSale(true);
                setOrderStatusText('Completed');
            } else if (calledFrom === 'OPEN_ORD') {
                setSaveAsCompletedSale(false);
                setOrderStatusText('Pending');
            } else if (threadID && (uiStates.fetchedThreadData || prefilledData)) {
                const currentData = uiStates.fetchedThreadData || prefilledData;
                const isCompleted = Number(currentData.StatusID ?? currentData.statusID ?? currentData.Sts ?? 0) === 2;
                setSaveAsCompletedSale(isCompleted);
                if (isCompleted) {
                    setOrderStatusText('Completed');
                } else {
                    const currStageName = currentData.CurrStage || currentData.currStage || currentData.Status || currentData.status;
                    setOrderStatusText(currStageName || 'Pending');
                }
            } else if (!threadID && threadTypeID === 2) {
                setSaveAsCompletedSale(false);
                setOrderStatusText('');
            }
        }
    }, [isOpen, calledFrom, threadID, threadTypeID, uiStates.fetchedThreadData, prefilledData]);

    useEffect(() => {
        if (!formData.connID) return;
        const secondaryContacts = masterData.connections
            .filter(conn => conn.ConnID != formData?.connID && (conn.IsCold !== 1 && conn.IsCold !== true))
            .map((c) => {
                return {
                    value: c.ConnID,
                    label: !c.BizName || c.BizName.trim() === '-' ? c.ContactName : c.BizName
                }
            }).sort((a, b) => a.label.localeCompare(b.label))
        setSecondaryContacts(secondaryContacts)
    }, [formData?.connID, isOpen])

    useEffect(() => {
        if (!formData.connID || !prefsEnabled.contactPerson) return;

        const selectedConnection = masterData.connections.find(conn => conn.ConnID === formData.connID);
        if (!selectedConnection) return;

        const primaryContact = selectedConnection.ContactName;

        if (primaryContact) {
            setFormData(prev => {
                if (prev.contactPerson === primaryContact) return prev;
                return { ...prev, contactPerson: primaryContact };
            });
        }
    }, [formData?.connID, masterData.connections, prefsEnabled.contactPerson])

    // Transform database thread data into form-compatible format
    const transformThreadData = (threadFromDB) => {
        if (!threadFromDB) return null;
        const baseData = {
            threadID: threadFromDB.ThreadID || threadFromDB.threadID,
            connID: threadFromDB.ConnID || threadFromDB.connID,
            contactPerson: threadFromDB.ContactPerson || threadFromDB.contactPerson || threadFromDB.connectionData?.ContactName || threadFromDB.connectionData?.contactName || '',
            product: threadFromDB.MainProduct || threadFromDB.mainProduct || threadFromDB.Product || threadFromDB.product || '',
            model: threadFromDB.Model || threadFromDB.model || '',
            reference: threadFromDB.Reference || threadFromDB.reference || '',
            quantity: threadFromDB.MainQty || threadFromDB.mainQty || threadFromDB.Quantity || threadFromDB.quantity || '',
            dueDT: (() => {
                if (!allowOpenOrders && isInqOnlyMode && threadTypeID === 2) {
                    const dt = threadFromDB.SinceDT || threadFromDB.sinceDT;
                    return dt ? format(new Date(dt), 'dd-MMM-yy') : '';
                }
                const dt = threadFromDB.DueDT || threadFromDB.dueDT;
                if (!dt) return '';
                try {
                    return format(new Date(dt), 'dd-MMM-yy');
                } catch (e) {
                    return dt;
                }
            })(),
            businessValue: threadFromDB.BizProspect !== undefined ? threadFromDB.BizProspect : (threadFromDB.bizProspect !== undefined ? threadFromDB.bizProspect : (threadFromDB.businessValue !== undefined ? threadFromDB.businessValue : '')),
            pocUserID: (threadFromDB.POCUserID !== undefined && threadFromDB.POCUserID !== null) ? threadFromDB.POCUserID.toString() : ((threadFromDB.pocUserID !== undefined && threadFromDB.pocUserID !== null) ? threadFromDB.pocUserID.toString() : ''),
            nextTaskDT: (() => {
                const dt = threadFromDB.NextApptDT || threadFromDB.nextApptDT;
                if (!dt) return '';
                try {
                    return format(new Date(dt), 'dd-MMM-yy');
                } catch (e) {
                    return dt;
                }
            })(),
            nextTaskTime: (() => {
                const dt = threadFromDB.NextApptDT || threadFromDB.nextApptDT;
                if (!dt) return '';
                try {
                    return format(new Date(dt), 'HH:mm');
                } catch (e) {
                    return dt;
                }
            })(),
            nextTaskType: threadFromDB.NextTaskTyp || threadFromDB.nextTaskTyp || '',
            nextTaskNotes: threadFromDB.NextTask || threadFromDB.nextTask || '',
            notes: threadFromDB.Notes || threadFromDB.notes || '',
            sinceDT: (() => {
                const dt = threadFromDB.SinceDT || threadFromDB.sinceDT;
                if (!dt) return '';
                try {
                    return format(new Date(dt), 'dd-MMM-yy HH:mm:ss');
                } catch (e) {
                    return dt;
                }
            })(),
            source: threadFromDB.Source || threadFromDB.source || '',
            originalFileName: threadFromDB.ThrFile || threadFromDB.thrFile || '',
            statusID: threadFromDB.StatusID !== undefined ? threadFromDB.StatusID : (threadFromDB.statusID !== undefined ? threadFromDB.statusID : (threadFromDB.Sts !== undefined ? threadFromDB.Sts : threadFromDB.sts)),
            currStageID: threadFromDB.CurrStageID || threadFromDB.currStageID,
            currStage: threadFromDB.CurrStage || threadFromDB.currStage || threadFromDB.Status || threadFromDB.status || '',
            secConnIDs: threadFromDB.SecConnIDs ? (typeof threadFromDB.SecConnIDs === 'string' ? threadFromDB.SecConnIDs.split(",").map(id => Number(id)).filter(Boolean) : (Array.isArray(threadFromDB.SecConnIDs) ? threadFromDB.SecConnIDs : [])) : []
        };

        // Set fileName for UI display if file exists
        const fileName = threadFromDB.ThrFile || threadFromDB.thrFile;
        if (fileName) {
            const displayFileName = typeof fileName === 'string' && fileName.includes('/')
                ? fileName.split('/').pop()
                : fileName;
            setUIStates(prev => ({ ...prev, fileName: displayFileName }));
        }

        try {
            const customFieldsRaw = threadFromDB?.CustomFields || threadFromDB?.customFields || threadFromDB?.ExtraFields || threadFromDB?.extraFields;
            const customFieldsString = typeof customFieldsRaw === 'string' ? customFieldsRaw.trim() : (Array.isArray(customFieldsRaw) ? JSON.stringify(customFieldsRaw) : '');

            if (customFieldsString && customFieldsString !== '' && customFieldsString !== '[]') {
                const customFields = JSON.parse(customFieldsString);

                if (Array.isArray(customFields)) {
                    customFields.forEach(field => {
                        const fieldId = `extraField_${field.FID}`;
                        baseData[fieldId] = field.V || "";
                    });
                }
            }
        } catch (error) {
            console.error("Error parsing thread CustomFields:", error);
        }

        if (masterData.thrExtraFields && masterData.thrExtraFields.length > 0) {
            const typeIDToMatch = threadFromDB.TypeID || threadFromDB.typeID || threadTypeID;
            const currentThreadFields = masterData.thrExtraFields.find(tf => tf.TID === typeIDToMatch);
            if (currentThreadFields && currentThreadFields.Fields) {
                let fieldsArray = [];
                try {
                    fieldsArray = typeof currentThreadFields.Fields === 'string'
                        ? JSON.parse(currentThreadFields.Fields)
                        : currentThreadFields.Fields;
                } catch (error) {
                    console.error("Error parsing thread extra fields in transform:", error);
                    fieldsArray = [];
                }

                if (Array.isArray(fieldsArray) && fieldsArray.length > 0) {
                    fieldsArray.forEach(field => {
                        if (!field.IsD) { // Only active fields
                            const fieldId = `extraField_${field.FID}`;
                            // Only set to empty if not already populated from CustomFields
                            if (baseData[fieldId] === undefined) {
                                baseData[fieldId] = "";
                            }
                        }
                    });
                }
            }
        }

        return baseData;
    };

    // Validate form data before submission with comprehensive rules
    const validateFormData = () => {
        const validationRules = [
            {
                field: 'connID',
                condition: !formData.connID,
                message: 'Please select a contact.',
                description: 'Contact is required.'
            },
            {
                field: 'product',
                condition: (!formData.product || formData.product === '') && threadTypeID !== 1,
                message: 'Please select a product.',
                description: 'Product is required.'
            },
            {
                field: 'orderStatus',
                condition: threadTypeID === 2 && !orderStatusText?.trim(),
                message: 'Please select a status.',
                description: 'Status is required.'
            },
            {
                field: 'dueDT',
                condition: !formData.dueDT && threadTypeID !== 2 && !hideFields.dueDT,
                message: 'Please select target date.',
                description: 'Target date is required.'
            },
            {
                field: 'businessValue',
                condition: false,
                message: 'Please enter business value.',
                description: 'Business value is required.'
            },
        ];

        for (const rule of validationRules) {
            if (rule.condition) {
                return {
                    isValid: false,
                    field: rule.field,
                    message: rule.message,
                    description: rule.description
                };
            }
        }

        // Additional target date validation
        if (formData.dueDT && threadTypeID !== 2 && !hideFields.dueDT) {
            const targetDate = new Date(formData.dueDT);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            targetDate.setHours(0, 0, 0, 0);

            if (targetDate < today && !threadID) {
                return {
                    isValid: false,
                    message: 'Please select valid target date.',
                    description: 'Target date cannot be in the past. Please select today or a future date.'
                };
            }
        }

        // Next task validation - if one is filled, both must be filled
        if (prefsEnabled.nextTask && !threadID) {
            const hasNextTaskType = formData.nextTaskType && formData.nextTaskType.trim() !== '';
            const hasNextTaskNotes = formData.nextTaskNotes && formData.nextTaskNotes.trim() !== '';

            if (hasNextTaskType && !hasNextTaskNotes) {
                return {
                    isValid: false,
                    field: 'nextTaskNotes',
                    message: 'Please enter Task Details.',
                    description: 'Task Details is required when next task is selected.'
                };
            }

            if (hasNextTaskNotes && !hasNextTaskType) {
                return {
                    isValid: false,
                    field: 'nextTaskType',
                    message: 'Please select next task.',
                    description: 'Next task is required when Task Details are entered.'
                };
            }
        }

        return { isValid: true };
    };
    const shouldAutoOpen = false;
    const [isAutoOpeningContact, setIsAutoOpeningContact] = useState(false);
    // Handle input field changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Handle file selection
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const isSaleEntry = threadTypeID === 2 || threadTypeID === 5;

            // Enforce 1 MB limit for sales
            if (isSaleEntry && file.size > 1024 * 1024) {
                toast.error("File size must be up to 1 MB only.");
                event.target.value = "";
                return;
            }

            if (isSaleEntry) {
                // Sales support all document/file types up to 1 MB
                setFormData(prev => ({ ...prev, file }));
                setUIStates(prev => ({ ...prev, fileName: file.name }));
            } else {
                const allowedTypes = [
                    "application/pdf",
                    "image/jpeg",
                    "image/jpg",
                    "image/png",
                    "image/webp"
                ];

                if (allowedTypes.includes(file.type)) {
                    setFormData(prev => ({ ...prev, file }));
                    setUIStates(prev => ({ ...prev, fileName: file.name }));
                } else {
                    toast.error("Please select valid file format.", {
                        description: "Only PDF and image files (JPEG, PNG, WebP) are allowed"
                    });
                    event.target.value = "";
                }
            }
        }
    };

    // Handle dropdown selections and manage dialog state transitions
    const handleSelectChange = (fieldName, value, selectedConnection = null, searchText = null) => {
        if (fieldName === 'connID' && (threadID || fromConnectionDetail)) return;

        if (fieldName === 'pocUserID') {
            setUIStates(prev => ({ ...prev, userHasInteractedWithPocField: true }));
        }

        if (value === 'add') {
            const masterTypeMap = {
                product: 'products',
                source: 'sources',
                nextTaskType: 'taskTypes'
            };

            if (masterTypeMap[fieldName]) {
                setSelectedField(fieldName);
                setMasterDialogType(masterTypeMap[fieldName]);
                setUIStates(prev => ({ ...prev, masterSearchText: searchText || '' }));
                setDialogStates(prev => ({ ...prev, masterDialog: true }));
            } else if (fieldName === 'model') {
                setDialogStates(prev => ({ ...prev, modelDialog: true, modelSearchText: searchText || '' }));
            } else if (fieldName === 'connID') {
                // Store the search text for prefilling business name
                setUIStates(prev => ({ ...prev, connectionSearchText: searchText || '' }));
                // Flag to indicate this came from Thread Entry (blocks Inquiry section in ConnectionEntry)
                setDialogStates(prev => ({ ...prev, connection: true, fromThreadEntry: true }));
                // setIsOpen(false);
            }
        } else if (fieldName === 'product' && value !== 'add') {
            // When product changes, clear the model field
            setFormData(prev => ({
                ...prev,
                product: value,
                model: ''
            }));
        } else if (fieldName === 'connID' && selectedConnection) {
            let lastDetails = [];
            try {
                // Parse the new column from the selected connection object
                lastDetails = typeof selectedConnection.LastThrDetails === 'string'
                    ? JSON.parse(selectedConnection.LastThrDetails)
                    : (selectedConnection.LastThrDetails || []);
            } catch (e) {
                lastDetails = [];
            }

            // Find details matching current thread type (TID)
            const tidDetails = Array.isArray(lastDetails)
                ? lastDetails.find(d => d.TID === threadTypeID)
                : null;

            setFormData(prev => ({
                ...prev,
                [fieldName]: value,
                product: prev.product || tidDetails?.Prd || '',
                model: prev.model || tidDetails?.Mdl || '',
                businessValue: prev.businessValue || tidDetails?.Val || '',
                pocUserID: (prev.pocUserID === "0" || !prev.pocUserID) ? (tidDetails?.POC?.toString() || prev.pocUserID) : prev.pocUserID,
                contactPerson: (() => {
                    if (!value) return '';
                    let contacts = selectedConnection.Contacts;
                    if (typeof contacts === 'string') { try { contacts = JSON.parse(contacts); } catch (e) { contacts = []; } }
                    if (contacts && Array.isArray(contacts) && contacts.length === 1) return contacts[0].CNm || '';
                    return selectedConnection.ContactName || selectedConnection.BizName || '';
                })()
            }));
        } else {
            setFormData(prev => ({ ...prev, [fieldName]: value }));
        }
    };


    // Handle date changes with proper formatting for different date types
    const handleDateChange = (fieldID, date) => {
        let formattedDate = '';

        if (date) {
            if (fieldID === 'sinceDT') {
                const currentTime = new Date();
                const selectedDate = new Date(date);
                selectedDate.setHours(currentTime.getHours(), currentTime.getMinutes(), currentTime.getSeconds());
                formattedDate = format(selectedDate, 'dd-MMM-yy HH:mm:ss');
            } else {
                formattedDate = format(date, 'dd-MMM-yy');
            }
        }

        setFormData(prev => ({ ...prev, [fieldID]: formattedDate }));
        setUIStates(prev => ({
            ...prev,
            datePickerStates: { ...prev.datePickerStates, [fieldID]: false }
        }));

        if (fieldID === "nextTaskDT" && prefsEnabled.nextTask && !threadID && formData.nextTaskTime) {
            const validation = validateTaskDateTime(formattedDate, formData.nextTaskTime, true);
            if (!validation.isValid) {
                const futureTimeString = getNextHourTime();
                setFormData(prev => ({ ...prev, nextTaskTime: futureTimeString }));
            }
        }
    };

    // Handle time changes with validation for future task scheduling
    const handleTimeChange = (fieldName, timeValue) => {
        setFormData(prev => ({ ...prev, [fieldName]: timeValue }));

        if (fieldName === "nextTaskTime" && prefsEnabled.nextTask && !threadID && formData.nextTaskDT) {
            const validation = validateTaskDateTime(formData.nextTaskDT, timeValue, true);
            if (!validation.isValid) {
                toast.error(validation.message, {
                    description: validation.description,
                });
                const futureTimeString = getNextHourTime();
                setFormData(prev => ({ ...prev, [fieldName]: futureTimeString }));
            }
        }
    };

    // Handle business value input with numeric validation and limits
    const handleBusinessValueChange = (e) => {
        const value = e.target.value;
        if (value === '') {
            handleInputChange(e);
            return;
        }
        const numericValue = value.replace(/[^0-9.]/g, '');
        const parts = numericValue.split('.');
        if (parts.length > 2 || (parts[1] && parts[1].length > 2)) return;
        const numberValue = parseFloat(numericValue);
        const maxValue = 999999999.99;
        if (!isNaN(numberValue) && numberValue > maxValue) return;
        const syntheticEvent = { target: { name: e.target.name, value: numericValue } };
        handleInputChange(syntheticEvent);
    };

    const handleBizProspectConfirm = () => {
        setDialogStates(prev => ({ ...prev, bizProspectConfirm: false }));
        handleSaveThreadWithoutConfirmation();
    };

    const handleBizProspectCancel = () => {
        setDialogStates(prev => ({ ...prev, bizProspectConfirm: false }));
    };

    // Save or update thread data with comprehensive validation
    const handleSaveThread = async () => {
        const validation = validateFormData();
        if (!validation.isValid) {
            toast.error(validation.message, {
                description: validation.description,
            });
            return;
        }

        const businessValue = parseFloat(formData.businessValue) || 0;
        if (businessValue === 0) {
            if (threadTypeID == 1 && !threadID) {
                setDialogStates(prev => ({ ...prev, bizProspectConfirm: false }));
            } else {
                setDialogStates(prev => ({ ...prev, bizProspectConfirm: true }));
                return;
            }
        }

        handleSaveThreadWithoutConfirmation();
    };

    const handleSaveThreadWithoutConfirmation = async () => {
        const hasNextTaskData = (prefsEnabled.nextTask && !threadID) &&
            (formData.nextTaskNotes && formData.nextTaskType);

        let uploadedFileName = '';

        // Handle file upload if file is selected
        if (formData.file) {
            try {
                const uploadResult = await apiService.uploadFile(
                    formData.file,
                    "Threads",
                    "both");

                if (uploadResult.error) {
                    toast.error(uploadResult.message);
                    return;
                }
                uploadedFileName = uploadResult.data.relativePath;
            } catch (uploadError) {
                console.error('File upload error:', uploadError);
                toast.error('Failed to upload file');
                return;
            }
        }

        // Determine final file name - use new upload, existing file, or empty
        let finalFileName = '';
        if (uploadedFileName) {
            // New file was uploaded
            finalFileName = uploadedFileName;
        } else if (formData.originalFileName && !formData.file) {
            // Keep existing file (no new file selected and original exists)
            finalFileName = formData.originalFileName;
        }

        if (forceOpen && formData.product && formData.product.trim() !== '') {
            const productName = formData.product.trim();

            const productAdded = await addNewProduct(productName);
            if (!productAdded) {
                return;
            }
        }

        const extraFieldsArray = [];
        if (masterData.thrExtraFields && masterData.thrExtraFields.length > 0) {
            const currentThreadFields = masterData.thrExtraFields.find(tf => tf.TID === threadTypeID);
            if (currentThreadFields && currentThreadFields.Fields) {
                let fieldsArray = [];
                try {
                    fieldsArray = typeof currentThreadFields.Fields === 'string'
                        ? JSON.parse(currentThreadFields.Fields)
                        : currentThreadFields.Fields;
                } catch (error) {
                    console.error("Error parsing thread extra fields:", error);
                    fieldsArray = [];
                }

                if (Array.isArray(fieldsArray)) {
                    fieldsArray.forEach(field => {
                        if (!field.IsD) { // Only include active fields
                            const fieldId = `extraField_${field.FID}`;
                            const fieldValue = formData[fieldId];

                            if (fieldValue && fieldValue.trim() !== '') {
                                extraFieldsArray.push({
                                    FID: field.FID,
                                    V: fieldValue.trim()
                                });
                            }
                        }
                    });
                }
            }
        }

        const apiData = {
            typeID: threadTypeID,
            connID: formData.connID,
            contactPerson: formData.contactPerson,
            product: formData.product,
            model: formData.model,
            reference: formData.reference,
            quantity: formData.quantity,
            dueDT: (() => {
                // For order/sale entry (TID 2), use sinceDT date part as dueDT
                if (threadTypeID === 2) {
                    return formData.sinceDT ? formData.sinceDT.split(' ')[0] : (formData.dueDT ? formData.dueDT.split(' ')[0] : format(new Date(), 'dd-MMM-yy'));
                }
                if (calledFrom == 'THRDONE') {
                    return format(new Date(), 'dd-MMM-yy HH:mm:ss');
                }
                return formData.dueDT;
            })(),
            businessValue: formData.businessValue,
            pocUserID: formData.pocUserID,
            nextTaskDT: hasNextTaskData ? formData.nextTaskDT : null,
            nextTaskTime: hasNextTaskData ? formData.nextTaskTime : null,
            nextTaskType: hasNextTaskData ? (formData.nextTaskType || null) : null,
            nextTaskNotes: hasNextTaskData ? formData.nextTaskNotes : null,
            notes: formData.notes || '',
            sinceDT: calledFrom == 'THRDONE' ? format(new Date(), 'dd-MMM-yy HH:mm:ss') : formData.sinceDT,
            source: formData.source,
            stageID: formData.stageID || 0,
            threadID: threadID || 0,
            shareByEmail: formData.shareByEmail || false,
            currencySymbol: currencySymbol,
            markInquiryDone: prefilledData && prefilledData.inquiryThreadID ? true : false,
            inquiryThreadID: prefilledData && prefilledData.inquiryThreadID ? prefilledData.inquiryThreadID : 0,
            customFields: JSON.stringify(extraFieldsArray),
            thrFile: finalFileName,
            secConnIDs: formData?.secConnIDs?.join(",") || '',
            statusText: threadTypeID === 2 ? orderStatusText : ''
        };
        try {
            let operation = threadID ? 'EDIT' : 'ADD';
            if (threadID) {
                operation = 'EDIT';
            } else if (threadTypeID === 2) {
                operation = saveAsCompletedSale ? 'ORDDONE' : 'ADD';
            }
            else if (calledFrom === 'INQDONE' || calledFrom === 'THRDONE') {
                operation = saveAsCompletedSale ? 'ORDDONE' : 'ADD';
            }
            else if (calledFrom === 'THRREP') {
                operation = 'ADDDONEORD';
            }
            else if (calledFrom === 'ORDDONE') {
                operation = 'ORDDONE';
            }
            else if (extOperation != '') {
                operation = extOperation;
            }

            const result = await manageThreadAPI(apiData, operation, masterData.threadTypes);

            if (result.error == false && result.message == 'Success') {
                if (threadTypeID === 2 || calledFrom === 'INQDONE' || calledFrom === 'THRDONE' || calledFrom === 'ORDDONE' || operation === 'INQDONE' || operation === 'ADDDONEORD') {
                    // playOrderSound();
                }
                if (threadTypeID !== 2) {
                    toast.success(`${capitalizeFirstLetter(getThreadNameFromTypeID(threadTypeID, masterData.threadTypes))} ${threadID ? 'updated' : 'saved'}.`);
                }
                const selectedConnection = masterData.connections.find(conn => conn.ConnID === formData.connID);
                if (shareByWhatsApp && selectedConnection) {
                    const phone = selectedConnection.Mobile || "";
                    const countryCode = selectedConnection.CC || "91";
                    const currentUserID = authData?.UserID ? authData.UserID.toString() : "";
                    const baseUrl = isLive() ? "sahicrm.com" : "http://localhost:3000";

                    let savedThreadID = threadID;
                    if (!savedThreadID && result.updatePackage) {
                        try {
                            const updateData = JSON.parse(result.updatePackage);
                            const tableA = Object.values(updateData).find(table =>
                                Array.isArray(table) && table[0]?.Tbl === "A"
                            );
                            if (tableA && tableA.length > 0 && tableA[0]?.RetData) {
                                try {
                                    const retDataArr = JSON.parse(tableA[0].RetData);
                                    if (Array.isArray(retDataArr) && retDataArr.length > 0) {
                                        const lastRet = retDataArr[retDataArr.length - 1];
                                        if (lastRet?.ThreadID) {
                                            savedThreadID = lastRet.ThreadID;
                                        }
                                    }
                                } catch (e) { }
                            }

                            if (!savedThreadID) {
                                const threadsTable = Object.values(updateData).find(table =>
                                    Array.isArray(table) && table[0]?.Tbl === "Threads"
                                );
                                if (threadsTable && threadsTable.length > 0) {
                                    const lastThread = threadsTable[threadsTable.length - 1];
                                    savedThreadID = lastThread?.ThreadID;
                                }
                            }

                            if (!savedThreadID) {
                                const connsTable = Object.values(updateData).find(table =>
                                    Array.isArray(table) && table[0]?.Tbl === "Conns"
                                );
                                if (connsTable && connsTable.length > 0 && connsTable[0]?.LastThreadID) {
                                    savedThreadID = connsTable[0].LastThreadID;
                                }
                            }
                        } catch (error) {
                            console.error("Error parsing update package for tracking link:", error);
                        }
                    }

                    const trackingLink = threadTypeID === 2
                        ? `${baseUrl}/Web/order?aid=${agencyData?.AgencyID}&tid=${savedThreadID || ''}`
                        : `${baseUrl}/Web/route?i=${phone}&u=${currentUserID}`;

                    const referenceID = formData.reference ? ` (*#${formData.reference}*)` : "";

                    let message = "";

                    if (threadTypeID === 2) {
                        message = `*Sale Acknowledgment*\n\n` +
                            `Thank you for your Sale${referenceID} from ${selectedConnection?.BizName} to ${agencyData.AgencyName}.\n\n` +
                            `Track your order status here:\n${trackingLink}`;
                    } else if (threadTypeID === 3) {
                        // Ticket
                        message = `*Support Ticket Acknowledgment*\n\n` +
                            `Thank you for raising a Support Ticket${referenceID} with ${selectedConnection?.BizName}.\n\n` +
                            `Track your ticket status here:\n${trackingLink}`;
                    }

                    const whatsappUrl = `https://api.whatsapp.com/send?phone=${countryCode}${phone}&text=${encodeURIComponent(message)}`;
                    window.open(whatsappUrl, "_blank");
                }

                if (operation === 'ADD') {
                    const updateData = JSON.parse(result.updatePackage);

                    // Find the table with "Threads"
                    const threadsTable = Object.values(updateData).find(table =>
                        Array.isArray(table) && table[0]?.Tbl === "Threads"
                    );

                    let addedThread = null;
                    if (threadsTable && threadsTable.length > 0) {
                        const threadID = threadsTable[0].ThreadID;
                        setSelectedThreadID(threadID);
                        addedThread = threadsTable[0];
                    }
                    if (addedThread) {
                        if (finalFileName && !addedThread.ThrFile && !addedThread.thrFile) {
                            addedThread = { ...addedThread, ThrFile: finalFileName, thrFile: finalFileName };
                        }
                        setSelectedThreadForDetails(addedThread);
                    } else if (apiData) {
                        setSelectedThreadForDetails({
                            ...apiData,
                            ThreadID: selectedThreadID,
                            ThrFile: finalFileName,
                            thrFile: finalFileName
                        });
                    }
                    setIsOpen(false);
                    setDialogStates((prev) => ({ ...prev, threadDetails: true }))
                }

                // Call onThreadUpdated callback AFTER successful save and AWAIT it to ensure parent updates complete
                if (onThreadUpdated) {
                    await onThreadUpdated();
                }

                setFormData(getInitialFormData());
                setUIStates(prev => ({ ...prev, fileName: "" }));
                if (operation !== 'ADD') setIsOpen(false);
            }
        } catch (error) {
            console.error(`Error saving thread:`, error);
            const errorMessage = error.isBusinessLogicError
                ? error.message
                : `Failed to save ${getThreadNameFromTypeID(threadTypeID, masterData.threadTypes)}. Please try again or contact support if the issue persists.`;
            toast.error(errorMessage);
        }
    };

    // Delete thread with proper cleanup and confirmation
    const handleDeleteThread = async (operation) => {
        const currentConnID = formData.connID || uiStates.fetchedThreadData?.connID || preselectedConnID;
        // Validate that we have a connID
        if (!currentConnID) {
            return;
        }
        try {
            const deleteData = {
                typeID: threadTypeID,
                threadID: threadID,
                connID: currentConnID,
                contactPerson: formData.contactPerson,
                product: formData.product,
                model: formData.model,
                reference: formData.reference,
                quantity: formData.quantity,
                dueDT: formData.dueDT,
                businessValue: formData.businessValue,
                pocUserID: formData.pocUserID,
                nextTaskDT: formData.nextTaskDT,
                nextTaskTime: formData.nextTaskTime,
                nextTaskType: formData.nextTaskType || null,
                nextTaskNotes: formData.nextTaskNotes,
                notes: formData.notes || '',
                sinceDT: formData.sinceDT,
                source: formData.source,
                secConnIDs: (formData?.secConnIDs && formData?.secConnIDs.join(",")) || ''
            };

            await manageThreadAPI(deleteData, 'DLT', masterData.threadTypes);
            toast.success(`${getThreadNameFromTypeID(threadTypeID, masterData.threadTypes)} deleted.`);

            if (onThreadUpdated) {
                await onThreadUpdated();
            }

            // REMOVE this line: setDialogStates(prev => ({ ...prev, deleteAlert: false }));
            if (onThreadDeleted) {
                onThreadDeleted();
            }
            setIsOpen(false);
        } catch (error) {
            console.error('Error deleting thread:', error);
            const errorMessage = error.isBusinessLogicError
                ? error.message
                : 'Failed to delete thread. Please try again or contact support if the issue persists.';
            toast.error(errorMessage,);
        }
    };

    // Handle successful connection creation or editing
    const handleConnectionAdded = (savedConnection) => {
        // If Desktop, Force Open (Signup), and we just added a connection (which includes inquiry now), don't reopen ThreadEntry
        if (!isMobileLayout && forceOpen) {
            setDialogStates(prev => ({ ...prev, connection: false }));
            setIsOpen(false);
            if (onThreadUpdated) onThreadUpdated();
            return;
        }
        // if (savedConnection) {
        //     const connID = savedConnection.ConnID || savedConnection.connID;

        //     // Set the connection ID and contact person
        //     setFormData(prev => ({
        //         ...prev,
        //         connID: connID,
        //         contactPerson: prefsEnabled.contactPerson ? (savedConnection.ContactName || savedConnection.name || '') : prev.contactPerson
        //     }));

        //     // Force re-render to ensure dropdown shows the new selection
        //     setTimeout(() => {
        //         setFormData(prev => ({
        //             ...prev,
        //             connID: connID
        //         }));
        //     }, 100);
        // }

        if (savedConnection) {
            const connID = savedConnection.ConnID || savedConnection.connID;

            if (!isMobileLayout) {
                setFormData(prev => ({
                    ...prev,
                    connID: connID,
                    contactPerson: prefsEnabled.contactPerson ? (savedConnection.ContactName || savedConnection.name || '') : prev.contactPerson,
                    connectionDetails: false
                }));

                // Force re-render to ensure dropdown shows the new selection
                setTimeout(() => {
                    setFormData(prev => ({
                        ...prev,
                        connID: connID,
                        connectionDetails: false
                    }));
                }, 100);

                setDialogStates(prev => ({ ...prev, connection: false }));
                setIsOpen(true);
            } else if (threadTypeID === 2) {
                // If Ord Entry (TID 2) on mobile, don't redirect to details. 
                // Close connection dialog and show the Ord form.
                setFormData(prev => ({
                    ...prev,
                    connID: connID,
                    contactPerson: (savedConnection.ContactName || savedConnection.name || '')
                }));
                setDialogStates(prev => ({ ...prev, connection: false, connectionDetails: false }));
                setIsOpen(true);
            } else {
                setDialogStates(prev => ({
                    ...prev,
                    connection: false,
                    connectionDetails: true,
                    createdConnID: connID
                }));
                setIsOpen(false);
            }
        } else {
            // Fallback to original behavior if no saved connection
            setUIStates(prev => ({ ...prev, connectionSearchText: '' }));
            setDialogStates(prev => ({ ...prev, connection: false }));
            setIsOpen(true);
        }
    };

    // Handle master dialog closure
    const handleMasterDialogClose = () => {
        setDialogStates(prev => ({ ...prev, masterDialog: false }));
        setMasterDialogType(null);
        setSelectedField("");
        setUIStates(prev => ({ ...prev, masterSearchText: '' }));
    };

    const addNewProduct = async (productName) => {
        const newProductData = {
            name: productName,
            description: '',
            price: 0,
            offeringType: 1,
            models: [],
            allowInquiry: true,
            allowOrder: true,
            allowSupport: true
        };

        try {
            await manageProductAPI('add', newProductData);
            return true;
        } catch (error) {
            toast.error(`Failed to add product "${productName}".`);
            return false;
        }
    };

    // Load thread data from database for editing mode
    useEffect(() => {
        if (!isOpen) {
            setUIStates(prev => ({ ...prev, fetchedThreadData: null }));
            return;
        }

        if (!threadID) {
            if (prefilledData) {
                const transformedData = transformThreadData(prefilledData);
                setUIStates(prev => ({ ...prev, fetchedThreadData: transformedData }));
            } else {
                setUIStates(prev => ({ ...prev, fetchedThreadData: null }));
            }
            return;
        }

        setUIStates(prev => ({ ...prev, isLoadingThread: true }));

        try {
            // Use synchronous function from Redux store
            const threadFromDB = getThreadByID(threadID);

            if (threadFromDB) {
                const transformedData = transformThreadData(threadFromDB);
                setUIStates(prev => ({ ...prev, fetchedThreadData: transformedData }));
            } else if (prefilledData) {
                const transformedData = transformThreadData(prefilledData);
                setUIStates(prev => ({ ...prev, fetchedThreadData: transformedData }));
            } else {
                console.warn(`Thread with ID ${threadID} not found in Redux store`);
                setUIStates(prev => ({ ...prev, fetchedThreadData: null }));
            }
        } catch (error) {
            console.error('Error loading thread data:', error);
            if (prefilledData) {
                try {
                    const transformedData = transformThreadData(prefilledData);
                    setUIStates(prev => ({ ...prev, fetchedThreadData: transformedData }));
                } catch (e) {
                    setUIStates(prev => ({ ...prev, fetchedThreadData: null }));
                }
            } else {
                setUIStates(prev => ({ ...prev, fetchedThreadData: null }));
            }
        } finally {
            setUIStates(prev => ({ ...prev, isLoadingThread: false }));
        }
    }, [threadID, isOpen, threadData, prefilledData]);



    const handleModelAdd = async () => {
        const modelName = dialogStates.modelSearchText;
        if (!modelName || !modelName.trim()) {
            toast.error("Please enter a name.");
            return;
        }

        const selectedProduct = masterData.products.find(p =>
            (p.name || p.PNm) === formData.product
        );

        if (!selectedProduct) {
            toast.error("Product not found.");
            return;
        }

        let models = [];
        try {
            const modelsData = selectedProduct.Mdls || selectedProduct.mdls || selectedProduct.Models || selectedProduct.models;
            if (modelsData) {
                models = typeof modelsData === 'string' ? JSON.parse(modelsData) : modelsData;
            }
        } catch (e) { models = []; }

        if (!Array.isArray(models)) models = [];

        if (models.some(m => (m.MNm || m.mNm || '').toLowerCase() === modelName.trim().toLowerCase())) {
            toast.error("This option already exists.");
            return;
        }

        const newID = models.length > 0 ? Math.max(...models.map(m => m.MID || m.mID || 0)) + 1 : 1;
        const newModel = { MID: newID, MNm: modelName.trim() };
        const updatedModels = [...models, newModel];

        const productPayload = {
            pid: selectedProduct.id || selectedProduct.PID,
            name: selectedProduct.name || selectedProduct.PNm,
            description: selectedProduct.D || selectedProduct.Desc || '',
            price: selectedProduct.Rate || selectedProduct.Price || 0,
            offeringType: selectedProduct.PTID || selectedProduct.offeringType || 1,
            models: updatedModels,
            imageUrl: selectedProduct.Img || selectedProduct.ImgURL || '',
            acpt: selectedProduct.Acpt || '',
            allowInquiry: (selectedProduct.Acpt || '').includes('I'),
            allowOrder: (selectedProduct.Acpt || '').includes('O'),
            allowSupport: (selectedProduct.Acpt || '').includes('S'),
        };

        try {
            await manageProductAPI('update', productPayload);
            // toast.success("Added");
            setFormData(prev => ({ ...prev, model: modelName.trim() }));
            setDialogStates(prev => ({ ...prev, modelDialog: false, modelSearchText: '' }));
        } catch (error) {
            console.error(error);
            toast.error("Failed to add.");
        }
    };

    // Render form fields based on type with consistent styling
    const renderFormField = (fieldID, label, placeholder, type = "text", required = false, maxLength, disabled = false) => {
        const prefsEnabledFields = {
            contactPerson: prefsEnabled.contactPerson,
            model: prefsEnabled.model,
            quantity: prefsEnabled.quantity,
            pocUserID: prefsEnabled.pocUserID || prefsEnabled.nextTask,
            nextTaskDT: prefsEnabled.nextTask,
            nextTaskTime: prefsEnabled.nextTask,
            nextTaskType: prefsEnabled.nextTask,
            nextTaskNotes: prefsEnabled.nextTask,
            sinceDT: prefsEnabled.sinceDT,
            source: prefsEnabled.source
        };

        // Replace the shouldShow logic in renderFormField function with this:

        const shouldShow = (() => {
            // Get current thread type info
            const currentThreadType = masterData.threadTypes.find(tt => tt.TID == threadTypeID);
            const isInquiryType = currentThreadType?.SnSng === "Inquiry";

            // Always show these core fields
            if (['product', 'dueDT', 'businessValue', 'notes', 'orderStatus', 'file'].includes(fieldID)) return true;

            // Special handling for sinceDT, source, quantity, pocUserID
            if (['sinceDT', 'source', 'quantity', 'pocUserID'].includes(fieldID)) {
                if (fieldID === 'sinceDT') {
                    return prefsEnabled.sinceDT || (isInqOnlyMode && calledFrom === 'ORDDONE');
                }
                if (fieldID === 'source') {
                    return prefsEnabled.source;
                }
                if (fieldID === 'quantity') {
                    const selectedProductObj = masterData.products.find(p => (p.name || p.PNm) === formData.product);
                    return formData.product && ((selectedProductObj?.PTID || selectedProductObj?.offeringType || 1) === 1);
                }
                if (fieldID === 'pocUserID') {
                    if (threadTypeID === 2) return true;
                    if (isSmartSellerMode()) return false;
                    return prefsEnabled.pocUserID;
                }
            }

            // Handle stageID field - only for TID 1 with Inquiry or Sales Inquiry
            if (fieldID === 'stageID') {
                if (threadTypeID === 1) {
                    const threadTypeName = currentThreadType?.SnSng;
                    return threadTypeName === 'Inquiry' || threadTypeName === 'Sales Inquiry';
                }
                return false;
            }

            if (fieldID === 'model') {
                if (formData.product) {
                    const selectedProduct = masterData.products.find(p => (p.name || p.PNm) === formData.product);
                    if (selectedProduct) {
                        try {
                            const modelsData = selectedProduct.Mdls || selectedProduct.mdls || selectedProduct.Models || selectedProduct.models;
                            if (modelsData) {
                                const models = typeof modelsData === 'string' ? JSON.parse(modelsData) : modelsData;
                                if (Array.isArray(models) && models.length > 0) {
                                    return true;
                                }
                            }
                        } catch (e) {
                        }
                    }
                }
                return prefsEnabled.model;
            }

            // Reference field logic - optional for Inquiry types, always visible for others
            if (fieldID === 'reference') {
                if (isInquiryType) {
                    return prefsEnabled.reference; // Show based on preference for Inquiry
                }
                return true; // Always show for non-Inquiry types
            }

            // Hide connection field in edit mode OR when preselectedConnID is provided
            if (fieldID === 'connID' && (threadID || preselectedConnID)) return false;

            // Show connection field in create mode when no preselectedConnID
            if (fieldID === 'connID' && !threadID && !preselectedConnID) return true;

            // Handle other fields based on preferences
            return prefsEnabledFields[fieldID];
        })();

        if (!shouldShow) {
            return null;
        }
        const isConnectionLocked = fieldID === 'connID' && (threadID || fromConnectionDetail);
        // const isRequiredField = fieldID === 'businessValue' ? threadTypeID !== 1 && threadTypeID !== 2 :
        //     fieldID === 'product' ? threadTypeID !== 1 :
        //         ['connID', 'dueDT'].includes(fieldID);

        const isRequiredField = fieldID === 'orderStatus' ? threadTypeID === 2 :
            fieldID === 'businessValue' ? false :
                fieldID === 'product' ? threadTypeID !== 1 :
                    ['connID', 'dueDT'].includes(fieldID);

        switch (type) {
            case 'connectionCombobox': {
                const nonColdConnections = masterData.connections.filter(
                    conn => conn.IsCold !== 1 && conn.IsCold !== true
                );
                const connectionOptions = getConnectionOptionsWithSearch(nonColdConnections);

                const selectedConnection = masterData.connections.find(conn => conn.ConnID === formData.connID);

                return (
                    <div key={fieldID} className={threadTypeID !== 1 ? "space-y-1.5" : threads_Classes.spacing}>
                        <Label htmlFor={fieldID} required={isRequiredField}>{label}</Label>
                        {isConnectionLocked ? (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className="sahi-card-lock">
                                            <span>{selectedConnection?.BizName || 'Contact Selected'}</span>
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>
                                            {fromConnectionDetail
                                                ? 'Contact already selected'
                                                : 'Contact cannot be changed in edit mode'
                                            }
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        ) : (
                            <CustomSearchableDropdown
                                options={connectionOptions}
                                value={formData.connID}
                                onSelect={(value, option, searchText) => handleSelectChange('connID', value, option?.connection, searchText)}
                                placeholder={placeholder}
                                searchPlaceholder="Search by name, city, or person."
                                displayKey="label"
                                valueKey="value"
                                allowAdd={canAddConnections}
                                addText="New Contact"
                                emptyMessage="No contacts found."
                                label="Contact"
                                enableMultiFieldSearch={true}
                                mobileDropdownText="Select Contact"
                                changeType='CONN'
                                showAddInHeader={true}
                                disabled={disabled}
                            />
                        )}
                    </div>
                );
            }

            case 'contactPersonCombobox': {
                const selectedConnection = masterData.connections.find(conn => conn.ConnID === formData.connID);

                // Hide the field if no connection is selected
                if (!selectedConnection) {
                    {/* return (
                        <div key={fieldID} className={threads_Classes.spacing}>
                            <Label htmlFor={fieldID} required={isRequiredField}>{label}</Label>
                            <CustomSearchableDropdown
                                options={[]}
                                value=""
                                onSelect={() => { }}
                                placeholder="Select contact first"
                                searchPlaceholder="Search contacts..."
                                displayKey="label"
                                valueKey="value"
                                allowAdd={false}
                                label="Contact Person"
                                emptyMessage="No contacts found."
                                disabled={true}
                            />
                        </div>
                    ); */}
                    return null;
                }

                let contacts = selectedConnection.Contacts;
                if (typeof contacts === 'string') {
                    try {
                        contacts = JSON.parse(contacts);
                    } catch (e) {
                        contacts = [];
                    }
                }

                let contactOptions = [];

                if (selectedConnection.ContactName) {
                    contactOptions.push({
                        value: selectedConnection.ContactName,
                        label: selectedConnection.ContactName
                    });
                }

                if (contacts && Array.isArray(contacts) && contacts.length > 0) {
                    const additionalContacts = contacts.map(contact => ({
                        value: contact.CNm,
                        label: contact.CNm
                    }));

                    const allContacts = [...contactOptions, ...additionalContacts];
                    contactOptions = allContacts.filter((contact, index, self) =>
                        index === self.findIndex(c => c.value === contact.value)
                    );
                }

                contactOptions.sort((a, b) => a.label.localeCompare(b.label));

                // Hide the field if there's only one or fewer contact options (length <= 1)
                if (contactOptions.length <= 1) {
                    return null;
                }

                return (
                    <div key={fieldID} className={threads_Classes.spacing}>
                        <Label htmlFor={fieldID} required={isRequiredField}>{label}</Label>
                        <CustomSearchableDropdown
                            options={contactOptions}
                            value={formData.contactPerson}
                            onSelect={(value) => handleSelectChange('contactPerson', value)}
                            placeholder={contactOptions.length === 0 ? "No contacts available" : placeholder}
                            searchPlaceholder="Search contacts..."
                            displayKey="label"
                            valueKey="value"
                            allowAdd={false}
                            label='Contact'
                            emptyMessage="No contacts found."
                            disabled={disabled}
                        />
                    </div>
                );
            }

            case 'productCombobox': {
                const productOptions = masterData.products
                    .map(product => ({
                        value: product.name || product.PNm,
                        label: product.name || product.PNm,
                        id: product.id || product.PID,
                        product: product // Store full product object for later use
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));

                return (
                    <div key={fieldID} className={threads_Classes.spacing}>
                        <Label htmlFor={fieldID} required={isRequiredField}>{label}</Label>
                        <CustomSearchableDropdown
                            options={productOptions}
                            value={formData.product}
                            onSelect={(value, option, searchText) => handleSelectChange('product', value, option, searchText)}
                            placeholder={placeholder}
                            searchPlaceholder="Search offering"
                            displayKey="label"
                            valueKey="value"
                            allowAdd={true}
                            label='Offering'
                            addText="Add Offering"
                            emptyMessage="No offerings found."
                            changeType={threadID && 'PRD'}
                            disabled={disabled}
                        />
                    </div>
                );
            }

            case 'modelCombobox': {
                // Find selected product to get its models/packages
                const selectedProduct = masterData.products.find(p =>
                    (p.name || p.PNm) === formData.product
                );

                if (!selectedProduct) {
                    {/* return (
                        <div key={fieldID} className={threads_Classes.spacing}>
                            <Label htmlFor={fieldID} required={isRequiredField}>{label}</Label>
                            <CustomSearchableDropdown
                                options={[]}
                                value=""
                                onSelect={() => { }}
                                placeholder="Select offering first"
                                searchPlaceholder="Search"
                                displayKey="label"
                                valueKey="value"
                                allowAdd={false}
                                label={label}
                                emptyMessage="No options available."
                                disabled={true}
                                changeType='PRD'
                            />
                        </div>
                    ); */}
                    return null
                }

                // Parse models array
                let models = [];
                try {
                    const modelsData = selectedProduct.Mdls || selectedProduct.mdls || selectedProduct.Models || selectedProduct.models;
                    if (modelsData) {
                        models = typeof modelsData === 'string' ? JSON.parse(modelsData) : modelsData;
                    }
                } catch (error) {
                    console.error('Error parsing models:', error);
                    models = [];
                }

                if (!Array.isArray(models) || models.length === 0) {
                    return null;
                }

                // Format model options and add "Other" at the end
                const modelOptions = [
                    ...(Array.isArray(models)
                        ? models.map(model => ({
                            value: model.mNm || model.MNm || '',
                            label: model.mNm || model.MNm || ''
                        })).filter(m => m.value)
                        : []),
                    {
                        value: 'Other',
                        label: 'Other'
                    }
                ];

                // Determine label based on offering type
                const offeringType = selectedProduct.PTID || selectedProduct.ptid || selectedProduct.offeringType || 1;
                const fieldLabel = offeringType === 1 ? 'Model' : 'Package';

                return (
                    <div key={fieldID} className={threads_Classes.spacing}>
                        <Label htmlFor={fieldID} required={isRequiredField}>{fieldLabel}</Label>
                        <CustomSearchableDropdown
                            options={modelOptions}
                            value={formData.model}
                            onSelect={(value, option, searchText) => handleSelectChange('model', value, option, searchText)}
                            placeholder={offeringType === 1 ? "Select Model" : "Select Package"}
                            searchPlaceholder={`Search ${offeringType === 1 ? 'models' : 'packages'}`}
                            displayKey="label"
                            valueKey="value"
                            allowAdd={true}
                            addText={`Add ${fieldLabel}`}
                            label={fieldLabel}
                            emptyMessage={`No ${offeringType === 1 ? 'models' : 'packages'} found.`}
                            disabled={disabled}
                        />
                    </div>
                );
            }
            case 'qtyText': {
                // Find selected product to get its models/packages
                const selectedProduct = masterData.products.find(p =>
                    (p.name || p.PNm) === formData.product
                );

                if (!selectedProduct) {
                    return;
                }

                // Determine label based on offering type - Default to 1 (Product) if undefined to match visibility logic
                const isProduct = (selectedProduct.PTID || selectedProduct.offeringType || 1) == 1;
                if (isProduct) {
                    return (
                        <div key={fieldID} className={threads_Classes.spacing}>
                            <Label htmlFor={fieldID} required={isRequiredField}>{label}</Label>
                            <Input
                                id={fieldID}
                                name={fieldID}
                                value={formData[fieldID]}
                                onChange={handleInputChange}
                                placeholder={placeholder}
                                className={threads_Classes.input}
                                type={'text'}
                                autoComplete={fieldID === 'quantity' ? 'off' : undefined}
                                maxLength={maxLength}
                                disabled={disabled}
                            />
                        </div>
                    );
                }
                break;
            }
            case 'sourceCombobox': {
                const sourceOptions = masterData.sources
                    ?.filter(item => item.TID === 1)
                    .flatMap(item =>
                        item.Srcs?.map(src => ({
                            value: src.Src,
                            label: src.Src
                        })) || []
                    ) || []
                        .sort((a, b) => a.label.localeCompare(b.label));

                return (
                    <div key={fieldID} className={threads_Classes.spacing}>
                        <Label htmlFor={fieldID} required={isRequiredField}>{label}</Label>
                        <CustomSearchableDropdown
                            options={sourceOptions}
                            value={formData.source}
                            onSelect={(value, option, searchText) => handleSelectChange('source', value, option, searchText)}
                            placeholder={placeholder}
                            searchPlaceholder="Search sources"
                            displayKey="label"
                            valueKey="value"
                            allowAdd={true}
                            addText="Add Source"
                            label="Source"
                            emptyMessage="No sources found."
                            disabled={disabled}
                        />
                    </div>
                );
            }

            case 'stageCombobox': {
                const threadTypeStages = masterData.threadStages?.find(t => t.TID === threadTypeID)?.Stages || [];

                if (threadTypeStages.length === 0) {
                    return null;
                }

                return (
                    <div key={fieldID} className={threads_Classes.spacing}>
                        <Label htmlFor={fieldID} required={isRequiredField}>{label}</Label>
                        <Select
                            value={formData.stageID ? String(formData.stageID) : ""}
                            onValueChange={(val) => handleSelectChange('stageID', val ? Number(val) : null)}
                            disabled={disabled}
                        >
                            <SelectTrigger className={`h-11 text-sm ${threads_Classes.input}`}>
                                <span className={formData.stageID ? "text-foreground" : "text-gray-500"}>
                                    {formData.stageID
                                        ? threadTypeStages.find(s => s.SID === formData.stageID)?.S || placeholder
                                        : placeholder
                                    }
                                </span>
                            </SelectTrigger>
                            <SelectContent className="max-h-[200px] shadow-lg">
                                {threadTypeStages.map((stage) => (
                                    <SelectItem
                                        key={stage.SID}
                                        value={String(stage.SID)}
                                        className="cursor-pointer"
                                    >
                                        {stage.S}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                );
            }

            case 'taskTypeCombobox': {
                const taskOptions = masterData.taskTypes
                    .map(type => ({ value: type, label: type }))
                    .sort((a, b) => a.label.localeCompare(b.label));

                return (
                    <div key={fieldID} className={threads_Classes.spacing}>
                        <Label htmlFor={fieldID} required={false}>{label}</Label>
                        <CustomSearchableDropdown
                            options={taskOptions}
                            value={formData.nextTaskType}
                            onSelect={(value, option, searchText) => handleSelectChange('nextTaskType', value, option, searchText)}
                            placeholder={placeholder}
                            searchPlaceholder="Search task types..."
                            displayKey="label"
                            valueKey="value"
                            allowAdd={true}
                            addText="Add Task Type"
                            label="Type"
                            emptyMessage="No task types found."
                            disabled={disabled}
                        />
                    </div>
                );
            }

            case 'userCombobox': {
                const currentUserID = authData?.UserID ? authData.UserID.toString() : "";
                const thrViewTypeID = currentUserHasPermission('ThrViewTypeID');

                const usersToDisplay = thrViewTypeID === 1
                    ? filteredUsers
                    : filteredUsers.filter(user => String(user.userID) === currentUserID);

                const userOptions = [
                    ...usersToDisplay.map(user => ({
                        value: String(user.userID),
                        label: user.name
                    }))
                ].sort((a, b) => {
                    // Sort current user to top for easy identification
                    if (a.value === currentUserID) return -1;
                    if (b.value === currentUserID) return 1;
                    if (a.value === "") return -1;
                    if (b.value === "") return 1;
                    return a.label.localeCompare(b.label);
                });

                // Ensure current user is selected if no value is set
                let currentValue = formData.pocUserID === 0 || formData.pocUserID === "0" || !formData.pocUserID
                    ? currentUserID
                    : String(formData.pocUserID);

                // Update form data if current value was empty and we have a current user
                if ((!formData.pocUserID || formData.pocUserID === "0") && currentUserID && !threadID) {
                    setFormData(prev => ({ ...prev, pocUserID: currentUserID }));
                    currentValue = currentUserID;
                }

                return (
                    <div key={fieldID} className={threads_Classes.spacing}>
                        <Label htmlFor={fieldID} required={isRequiredField}>{label}</Label>
                        <CustomSearchableDropdown
                            options={userOptions}
                            value={currentValue}
                            onSelect={(value) => handleSelectChange('pocUserID', value)}
                            placeholder={placeholder}
                            searchPlaceholder="Search users"
                            displayKey="label"
                            valueKey="value"
                            allowAdd={false}
                            label="User"
                            emptyMessage="No users found."
                            cancellable={false}
                            disabled={disabled}
                        />
                    </div>
                );
            }

            case 'nextTaskDT':
            case 'date': {
                const dateValue = formData[fieldID] ? new Date(formData[fieldID]) : undefined;
                const isDatePickerOpen = uiStates.datePickerStates[fieldID] || false;
                const today = new Date();
                const currentYear = today.getFullYear();
                today.setHours(0, 0, 0, 0);

                let minDate, maxDate, defaultMonth, restrictPastDates, fromYear, toYear;

                if (fieldID === 'dueDT' || type === 'nextTaskDT') {
                    minDate = today;
                    maxDate = null;
                    fromYear = currentYear - 5;
                    toYear = currentYear + 25;
                    restrictPastDates = (date) => {
                        const dateOnly = new Date(date);
                        dateOnly.setHours(0, 0, 0, 0);
                        return dateOnly < today;
                    };
                    defaultMonth = dateValue || today;
                } else if (fieldID === 'sinceDT') {
                    minDate = null;
                    maxDate = today;
                    fromYear = 1900;
                    toYear = currentYear;
                    restrictPastDates = (date) => {
                        const dateOnly = new Date(date);
                        dateOnly.setHours(0, 0, 0, 0);
                        return dateOnly > today;
                    };
                    defaultMonth = dateValue || today;
                } else {
                    restrictPastDates = undefined;
                    minDate = null;
                    maxDate = null;
                    fromYear = currentYear - 10;
                    toYear = currentYear + 10;
                    defaultMonth = dateValue || new Date();
                }

                return (
                    <DatePicker
                        key={fieldID}
                        fieldId={fieldID}
                        value={dateValue}
                        onChange={(date) => handleDateChange(fieldID, date)}
                        placeholder={placeholder}
                        label={label}
                        required={isRequiredField}
                        className={threads_Classes.spacing}
                        disabled={disabled || restrictPastDates}
                        minDate={minDate}
                        maxDate={maxDate}
                        fromYear={fromYear}
                        toYear={toYear}
                        defaultMonth={defaultMonth}
                        onOpenChange={(open) => setUIStates(prev => ({
                            ...prev,
                            datePickerStates: { ...prev.datePickerStates, [fieldID]: open }
                        }))}
                        open={isDatePickerOpen}
                        allowDeselect={false}
                        showYearNavigation={true}
                    />
                );
            }

            case 'time':
                return (
                    <TimeInputWithDialog
                        key={fieldID}
                        fieldID={fieldID}
                        label={fieldID === 'nextTaskTime' ? '' : label}
                        value={formData[fieldID]}
                        onChange={(timeString) => {
                            if (fieldID === "nextTaskTime") {
                                handleTimeChange(fieldID, timeString);
                            } else {
                                setFormData(prev => ({ ...prev, [fieldID]: timeString }));
                            }
                        }}
                        placeholder={placeholder}
                        required={isRequiredField}
                        disabled={disabled}
                    />
                );

            case 'businessValue':
                return (
                    <BusinessInput
                        fieldID={fieldID}
                        label={label}
                        placeholder={placeholder}
                        value={formData[fieldID]}
                        onChange={handleBusinessValueChange}
                        required={isRequiredField}
                        maxDecimalPlaces={2}
                        maxIntegerDigits={18}
                        currencySymbol={IndianRupee}
                        className={threads_Classes.spacing}
                        disabled={disabled}
                    />
                );

            case 'orderStatus': {
                const isOpenOrderActive = !isEditingCompletedSale && (isEditMode || isOrderStagesExpanded);
                const isStatusDisabled = disabled || isEditingCompletedSale;

                return (
                    <div key={fieldID} className="space-y-1.5">
                        <Label htmlFor="thread-entry-order-status-input" required={isRequiredField} className="text-sm font-medium text-text-color">
                            {label}
                        </Label>

                        {/* Order Stage Chips */}
                        <div className="flex flex-wrap items-center gap-2 pt-0.5">
                            {visibleOrderStages.map((stage) => {
                                const isSelected = saveAsCompletedSale
                                    ? (stage.id === 'completed' || stage.name.toLowerCase() === 'completed')
                                    : (orderStatusText || '').trim().toLowerCase() === stage.name.toLowerCase();
                                const displayLabel = stage.label || stage.name;
                                return (
                                    <button
                                        key={stage.id}
                                        type="button"
                                        disabled={isStatusDisabled}
                                        onClick={() => handleOrderStatusSelect(isSelected ? '' : stage.name)}
                                        className={`inline-flex items-center text-[13px] font-medium px-3 py-1.5 rounded-[5px] border transition-all duration-200 ${isSelected
                                            ? 'bg-white border-sahi-primary-color text-sahi-primary-color shadow-xs font-semibold'
                                            : 'bg-white text-[#374151] border-gray-200 hover:bg-gray-50'
                                            } ${isStatusDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                                    >
                                        <span className="max-w-[140px] truncate">{displayLabel}</span>
                                    </button>
                                );
                            })}

                            {/* Selected Other Stage Chip */}
                            {selectedOtherStage && !isEditingCompletedSale && (
                                <button
                                    type="button"
                                    disabled={isStatusDisabled}
                                    onClick={() => handleOrderStatusSelect('')}
                                    className={`inline-flex items-center text-[13px] font-medium px-3 py-1.5 rounded-[5px] border transition-all duration-200 bg-white border-sahi-primary-color text-sahi-primary-color shadow-xs font-semibold ${isStatusDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                                >
                                    <span className="max-w-[140px] truncate">{selectedOtherStage.name}</span>
                                </button>
                            )}

                            {/* Others badge */}
                            {isOpenOrderActive && remainingOthersCount > 0 && (
                                <button
                                    type="button"
                                    disabled={isStatusDisabled}
                                    onClick={() => {
                                        setOrderStagesInitialView('list');
                                        setOrderStagesDialogOpen(true);
                                    }}
                                    className="inline-flex items-center text-[13px] font-medium px-3 py-1.5 rounded-[5px] border transition-all duration-200 border-gray-200 bg-white text-sub-text-color hover:bg-gray-50 cursor-pointer"
                                >
                                    +{remainingOthersCount} {remainingOthersCount === 1 ? 'other' : 'others'}
                                </button>
                            )}

                            {/* New Stage button */}
                            {isOpenOrderActive && (
                                <button
                                    type="button"
                                    disabled={isStatusDisabled}
                                    onClick={() => {
                                        setOrderStagesInitialView('add');
                                        setOrderStagesDialogOpen(true);
                                    }}
                                    className="inline-flex items-center gap-1 text-[13px] font-medium px-3 py-1.5 rounded-[5px] border border-dashed border-gray-300 bg-white text-sub-text-color hover:border-sahi-primary-color hover:text-sahi-primary-color transition-all duration-200 cursor-pointer"
                                >
                                    <Plus className="h-3.5 w-3.5" />
                                    New
                                </button>
                            )}
                        </div>

                        {/* Text Input Box with 350ms transition */}
                        <div
                            className={`grid transition-all duration-[350ms] ease-in-out ${
                                isOpenOrderActive ? 'grid-rows-[1fr] opacity-100 mt-1.5' : 'grid-rows-[0fr] opacity-0 mt-0 pointer-events-none'
                            }`}
                        >
                            <div className="overflow-hidden">
                                <Input
                                    id="thread-entry-order-status-input"
                                    value={orderStatusText}
                                    onChange={(e) => handleOrderStatusInputChange(e.target.value)}
                                    placeholder={placeholder || "e.g. Processing, In Production, Query..."}
                                    disabled={isStatusDisabled || !isOpenOrderActive}
                                    className="bg-white text-sm h-10 px-3 py-2 leading-normal placeholder:text-sm placeholder:leading-normal placeholder:text-muted-foreground"
                                />
                            </div>
                        </div>
                    </div>
                );
            }

            case 'notes':
            case 'textarea':
                return (
                    <div key={fieldID} className={threads_Classes.spacing}>
                        <div className="flex justify-between items-center">
                            <Label htmlFor={fieldID} required={isRequiredField}>{label}</Label>
                        </div>
                        <div className="relative">
                            {!isMobileLayout && fieldID === 'notes' ? (
                                <Input
                                    id={fieldID}
                                    name={fieldID}
                                    value={formData[fieldID] || ""}
                                    onChange={handleInputChange}
                                    placeholder={placeholder}
                                    autoComplete="off"
                                    maxLength={maxLength}
                                    className={`${threads_Classes.input} ${fieldID === 'notes' ? 'pr-20' : ''}`}
                                    type="text"
                                    disabled={disabled}
                                />
                            ) : (
                                <Textarea
                                    id={fieldID}
                                    name={fieldID}
                                    value={formData[fieldID] || ""}
                                    onChange={handleInputChange}
                                    placeholder={placeholder}
                                    autoComplete="off"
                                    maxLength={maxLength}
                                    className={`min-h-[60px] w-full resize-none text-sm ${fieldID === 'notes' ? 'pr-20' : ''}`}
                                    disabled={disabled}
                                    rows={2}
                                />
                            )}
                            {fieldID === 'notes' && (
                                <SpeechControls
                                    targetId={fieldID}
                                    value={formData[fieldID] || ""}
                                    onChange={(val) => setFormData(prev => ({ ...prev, [fieldID]: val }))}
                                    isMobile={isMobileLayout}
                                    className={!isMobileLayout ? "absolute right-2 top-1/2 -translate-y-1/2" : "absolute right-2 top-2"}
                                />
                            )}
                        </div>
                    </div>
                );

            case 'file':
                return (
                    <div key={fieldID} className={threads_Classes.spacing}>
                        <div className="flex justify-between items-center">
                            <Label htmlFor="file">{label}</Label>
                        </div>
                        <div className="relative">
                            <Input
                                id="file"
                                type="file"
                                accept={(threadTypeID === 2 || threadTypeID === 5) ? ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.txt" : ".pdf,.jpg,.jpeg,.png,.webp"}
                                onChange={handleFileChange}
                                className="hidden"
                                disabled={disabled}
                            />
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full justify-start text-left h-11 px-3 sahi-input text-sm"
                                onClick={() => document.getElementById("file").click()}
                                disabled={disabled}
                            >
                                {uiStates.fileName ? (
                                    <div className="flex items-center gap-2 truncate w-full">
                                        <FileText className={`${threads_Classes.iconSize} text-sahi-blue flex-shrink-0`} />
                                        <span className="truncate text-sm flex-1">{uiStates.fileName}</span>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="h-4 w-4 p-0 flex-shrink-0 hover:bg-gray-100 rounded-full"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setFormData(prev => ({ ...prev, file: null, originalFileName: "" }));
                                                setUIStates(prev => ({ ...prev, fileName: "" }));
                                                const fileInput = document.getElementById("file");
                                                if (fileInput) fileInput.value = "";
                                            }}
                                        >
                                            <X className="h-3 w-3" />
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 text-sub-text-color">
                                        <Upload className={threads_Classes.iconSize} />
                                        <span className="text-sm">Choose File</span>
                                    </div>
                                )}
                            </Button>
                        </div>
                    </div>
                );

            default:
                return (
                    <div key={fieldID} className={threads_Classes.spacing}>
                        <div className="flex justify-between items-center">
                            <Label htmlFor={fieldID} required={isRequiredField}>{label}</Label>
                            {/* {fieldID === 'reference' && lastRefInfo.hint && (
                                <span className="text-[10px] text-sub-text-color font-medium leading-none">{lastRefInfo.hint}</span>
                            )} */}
                        </div>
                        <Input
                            id={fieldID}
                            name={fieldID}
                            value={formData[fieldID]}
                            onChange={handleInputChange}
                            placeholder={fieldID === 'reference' && lastRefInfo.hint ? lastRefInfo.hint : placeholder}
                            className={threads_Classes.input}
                            type={type}
                            autoComplete={fieldID === 'quantity' ? 'off' : undefined}
                            maxLength={maxLength}
                            disabled={disabled}
                        />
                    </div>
                );
        }
    };

    const renderExtraFields = () => {
        if (!masterData.thrExtraFields || masterData.thrExtraFields.length === 0) {
            return null;
        }

        const currentThreadFields = masterData.thrExtraFields.find(tf => tf.TID === threadTypeID);
        if (!currentThreadFields || !currentThreadFields.Fields) {
            return null;
        }

        let fieldsArray = [];
        try {
            fieldsArray = typeof currentThreadFields.Fields === 'string'
                ? JSON.parse(currentThreadFields.Fields)
                : currentThreadFields.Fields;
        } catch (error) {
            console.error("Error parsing thread extra fields:", error);
            return null;
        }

        if (!Array.isArray(fieldsArray) || fieldsArray.length === 0) {
            return null;
        }

        // Filter out disabled fields
        const activeFields = fieldsArray.filter(field => !field.IsD);
        if (activeFields.length === 0) {
            return null;
        }
        // Arrange fields in rows - use 3 columns for normal mode
        const rows = [];
        const columnsPerRow = 3;
        for (let i = 0; i < activeFields.length; i += columnsPerRow) {
            rows.push(activeFields.slice(i, i + columnsPerRow));
        }

        return (
            <>
                {rows.map((row, rowIndex) => {
                    const isLastRow = rowIndex === rows.length - 1;
                    const marginClass = isLastRow ? '' : 'mb-4';
                    const baseRowClass = isMobileLayout
                        ? 'space-y-4'
                        : `grid ${threads_Classes.gridCols3} ${threads_Classes.gap4}`;
                    return (
                        <div key={`extra-row-${rowIndex}`} className={`${baseRowClass} ${marginClass}`}>
                            {row.map(field => {
                                const fieldId = `extraField_${field.FID}`;
                                const fieldValue = formData[fieldId] || '';

                                return (
                                    <div key={fieldId} className={threads_Classes.spacing}>
                                        <Label htmlFor={fieldId}>{field.F}</Label>
                                        <Input
                                            id={fieldId}
                                            name={fieldId}
                                            value={fieldValue}
                                            onChange={handleInputChange}
                                            placeholder={`Enter ${field.F.toLowerCase()}`}
                                            className={threads_Classes.input}
                                            type="text"
                                            autoComplete="off"
                                            maxLength={50}
                                            disabled={isDisabled}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </>
        );
    };


    // Arrange fields in logical rows for responsive layout
    const arrangeFieldsInRows = (fields) => {
        const rows = [];
        const find = (ids) => ids.map(id => fields.find(f => f.id === id)).filter(Boolean);

        const mandatoryFields = ['connID', 'product', 'dueDT', 'businessValue'];
        const isMinimalMode = fields.length <= 6 && fields.every(field =>
            mandatoryFields.includes(field.id) || field.id === 'notes' || field.id === 'reference'
        );

        if (isMinimalMode) {
            const usedFieldIDs = new Set();


            if (isInqOnlyMode) {
                const row1PrimaryFields = find(['connID']);
                const row1OptionalFields = find(['reference']);
                let row1Fields = [...row1PrimaryFields];

                if (row1Fields.length < 3 && row1OptionalFields.length > 0) {
                    row1Fields = [...row1Fields, ...row1OptionalFields];
                }

                if (row1Fields.length > 0) {
                    rows.push(row1Fields);
                    row1Fields.forEach(f => usedFieldIDs.add(f.id));
                }

                const productField = find(['product']);
                if (productField.length > 0) {
                    rows.push(productField);
                    productField.forEach(f => usedFieldIDs.add(f.id));
                }

                const row3Fields = find(['businessValue', 'dueDT']).filter(f => !usedFieldIDs.has(f.id));
                if (row3Fields.length > 0) {
                    rows.push(row3Fields);
                    row3Fields.forEach(f => usedFieldIDs.add(f.id));
                }
            } else {
                const row1PrimaryFields = find(['connID', 'product']);
                const row1OptionalFields = find(['reference']);
                let row1Fields = [...row1PrimaryFields];

                if (row1Fields.length < 3 && row1OptionalFields.length > 0) {
                    row1Fields = [...row1Fields, ...row1OptionalFields];
                }

                if (row1Fields.length > 0) {
                    rows.push(row1Fields);
                    row1Fields.forEach(f => usedFieldIDs.add(f.id));
                }

                const row2Fields = find(['businessValue', 'dueDT']).filter(f => !usedFieldIDs.has(f.id));
                if (row2Fields.length > 0) {
                    rows.push(row2Fields);
                    row2Fields.forEach(f => usedFieldIDs.add(f.id));
                }
            }

            const notesField = fields.find(f => f.id === 'notes');
            if (notesField) {
                rows.push([notesField]);
                usedFieldIDs.add('notes');
            }

            const remainingFields = fields.filter(f => !usedFieldIDs.has(f.id));
            for (let i = 0; i < remainingFields.length; i += 2) {
                const remainingRow = remainingFields.slice(i, i + 2);
                if (remainingRow.length > 0) {
                    rows.push(remainingRow);
                }
            }

            return rows;
        }

        // Complex layout for full mode
        const usedFieldIDs = new Set();

        const row1Ids = isMobileLayout ? ['connID', 'contactPerson'] : ['connID', 'contactPerson', 'reference'];
        const row1Available = find(row1Ids);
        if (row1Available.length > 0) {
            const row1Fields = [];

            if (threadID) {
                // Edit mode: Check if contact person should be shown
                const contactField = row1Available.find(f => f.id === 'contactPerson');
                const refField = row1Available.find(f => f.id === 'reference');

                // Check if contact person field should be disabled/hidden
                const selectedConnection = masterData.connections.find(conn => conn.ConnID === formData.connID);
                let shouldShowContactPerson = false;

                if (selectedConnection && contactField) {
                    let contacts = selectedConnection.Contacts;
                    if (typeof contacts === 'string') {
                        try {
                            contacts = JSON.parse(contacts);
                        } catch (e) {
                            contacts = [];
                        }
                    }

                    const contactOptions = [];
                    if (selectedConnection.ContactName) {
                        contactOptions.push(selectedConnection.ContactName);
                    }
                    if (contacts && Array.isArray(contacts)) {
                        contacts.forEach(contact => {
                            if (contact.CNm && !contactOptions.includes(contact.CNm)) {
                                contactOptions.push(contact.CNm);
                            }
                        });
                    }

                    // Show contact person if there are multiple options OR if it has a value and is not disabled
                    shouldShowContactPerson = contactOptions.length > 1 ||
                        (formData.contactPerson && contactOptions.length === 1);
                }

                if (shouldShowContactPerson && contactField) {
                    row1Fields.push(contactField);
                }
                if (refField) {
                    row1Fields.push(refField);
                }

                // If no fields to show in row 1, don't create the row
                if (row1Fields.length === 0) {
                    // Don't add this row, let product field start in row 2
                } else {
                    rows.push(row1Fields);
                    row1Fields.forEach(f => usedFieldIDs.add(f.id));
                }
            } else if (preselectedConnID) {
                // Entry mode with preselected connection: Show Contact Person and Reference, leave third space empty
                const contactField = row1Available.find(f => f.id === 'contactPerson');
                const refField = row1Available.find(f => f.id === 'reference');

                if (contactField) row1Fields.push(contactField);
                if (refField) row1Fields.push(refField);


                if (row1Fields.length > 0) {
                    rows.push(row1Fields);
                    row1Fields.forEach(f => usedFieldIDs.add(f.id));
                }
            } else {
                // Create mode: Show Connection, Contact Person, Reference (original logic)
                const connField = row1Available.find(f => f.id === 'connID');
                const contactField = row1Available.find(f => f.id === 'contactPerson');
                const refField = row1Available.find(f => f.id === 'reference');

                if (connField) row1Fields.push(connField);
                if (contactField) {
                    row1Fields.push(contactField);
                } else if (refField) {
                    row1Fields.push(refField);
                }

                if (row1Fields.length < 3) {
                    if (refField && !row1Fields.includes(refField)) {
                        row1Fields.push(refField);
                    }
                }

                if (row1Fields.length > 0) {
                    rows.push(row1Fields);
                    row1Fields.forEach(f => usedFieldIDs.add(f.id));
                }
            }
        }

        // Row 2: Product + Model + Status
        const productField = fields.find(f => f.id === 'product');
        const modelField = fields.find(f => f.id === 'model');
        const modelsData = (() => {
            const selectedProduct = masterData.products.find(p =>
                (p.name || p.PNm) === formData.product
            );
            if (!selectedProduct) return null;
            const rawModels = selectedProduct.Mdls || selectedProduct.mdls || selectedProduct.Models || selectedProduct.models;
            if (typeof rawModels === 'string') {
                try {
                    return JSON.parse(rawModels);
                } catch (e) {
                    return null;
                }
            }
            return rawModels;
        })();

        const hasModels = modelsData && Array.isArray(modelsData) && modelsData.length > 0;

        const statusField = fields.find(f => f.id === 'stageID');

        if (isMobileLayout) {
            if (productField) {
                rows.push([productField]);
                usedFieldIDs.add('product');
            }
            if (modelField && hasModels) {
                rows.push([modelField]);
                usedFieldIDs.add('model');
            } else if (modelField) {
            }
            // Status field moved to after business value on mobile (not here)

            // Mobile: Group Reference and Received On (SinceDT) in one row
            const refField = fields.find(f => f.id === 'reference');
            const sinceField = fields.find(f => f.id === 'sinceDT');
            const refSinceRow = [];

            if (refField && !usedFieldIDs.has('reference')) {
                refSinceRow.push(refField);
                usedFieldIDs.add('reference');
            }
            if (sinceField && !usedFieldIDs.has('sinceDT')) {
                refSinceRow.push(sinceField);
                usedFieldIDs.add('sinceDT');
            }

            if (refSinceRow.length > 0) {
                rows.push(refSinceRow);
            }
        } else {
            const row2Fields = [];
            if (productField) {
                row2Fields.push(productField);
                usedFieldIDs.add('product');
            }
            if (modelField && hasModels) {
                row2Fields.push(modelField);
                usedFieldIDs.add('model');
            } else if (modelField) {
            }
            if (statusField) {
                row2Fields.push(statusField);
                usedFieldIDs.add('stageID');
            }

            if (row2Fields.length > 0) {
                rows.push(row2Fields);
            }
        }

        // Row 3: Source field
        const sourceFieldForRow = fields.find(f => f.id === 'source' && !usedFieldIDs.has(f.id));

        // Row 4: Business Value + Quantity + Dates (combined)
        const businessValueField = fields.find(f => f.id === 'businessValue');
        const quantityField = fields.find(f => f.id === 'quantity');
        const dueDTField = fields.find(f => f.id === 'dueDT');
        const sinceDTField = fields.find(f => f.id === 'sinceDT' && !usedFieldIDs.has(f.id));
        const sourceField = fields.find(f => f.id === 'source' && !usedFieldIDs.has(f.id));

        if (businessValueField || quantityField || dueDTField || sinceDTField || sourceField) {
            const row4Fields = [];

            if (businessValueField) {
                row4Fields.push(businessValueField);
                usedFieldIDs.add('businessValue');
            }

            if (quantityField) {
                row4Fields.push(quantityField);
                usedFieldIDs.add('quantity');
            }

            if (dueDTField || sinceDTField) {
                row4Fields.push({
                    id: 'dueDT-sinceDT',
                    type: 'combined-dates',
                    fields: [dueDTField, sinceDTField].filter(Boolean)
                });
                if (dueDTField) usedFieldIDs.add('dueDT');
                if (sinceDTField) usedFieldIDs.add('sinceDT');
            }

            if (sourceFieldForRow) {
                row4Fields.push(sourceFieldForRow);
                usedFieldIDs.add('source');
            }
            if (row4Fields.length > 0) {
                rows.push(row4Fields);
            }
        }

        // On mobile, add status field after business value row
        if (isMobileLayout && statusField && !usedFieldIDs.has('stageID')) {
            rows.push([statusField]);
            usedFieldIDs.add('stageID');
        }

        // Row 5: Notes + Assignment
        const notesField = fields.find(f => f.id === 'notes');
        const pocUserField = fields.find(f => f.id === 'pocUserID');

        if (notesField || pocUserField) {
            const row5Fields = [];
            if (notesField) {
                row5Fields.push(notesField);
                usedFieldIDs.add('notes');
            }
            if (pocUserField) {
                row5Fields.push(pocUserField);
                usedFieldIDs.add('pocUserID');
            }
            rows.push(row5Fields);
        }

        // Row 6: Next Task fields 
        const nextTaskFields = find(['nextTaskDT', 'nextTaskTime', 'nextTaskType', 'nextTaskNotes']);
        if (nextTaskFields.length > 0) {
            rows.push(nextTaskFields);
            // Mark all next task fields as used to prevent them from being processed again
            nextTaskFields.forEach(f => usedFieldIDs.add(f.id));
        }

        // Handle remaining fields
        const remainingFields = fields.filter(f => !usedFieldIDs.has(f.id));
        for (let i = 0; i < remainingFields.length; i += 3) {
            const remainingRow = remainingFields.slice(i, i + 3);
            if (remainingRow.length > 0) {
                rows.push(remainingRow);
            }
        }

        return rows;
    };

    // Render dynamic form fields in organized layout
    // Complete renderDynamicFields function with product field always visible fix

    // Render dynamic form fields in organized layout
    // Complete renderDynamicFields function with product field always visible fix

    const renderDynamicFields = () => {
        // Get current thread type info
        const currentThreadType = masterData.threadTypes.find(tt => tt.TID == threadTypeID);
        const isInquiryType = currentThreadType?.SnSng === "Inquiry";
        const isMobileInquiry = isMobileLayout && threadTypeID === 1;

        let allFields = [
            { id: 'connID', label: 'Contact', placeholder: 'e.g. John Infrastructure', type: 'connectionCombobox', required: true, category: 'basic', priority: 1 },
            { id: 'contactPerson', label: 'Contact Person', placeholder: 'e.g. John Doe', type: 'contactPersonCombobox', required: false, category: 'basic', priority: 2, maxLength: 50 },
            { id: 'product', label: isInquiryType ? 'Offering Category' : 'Offering Category', placeholder: 'e.g. Laptop', type: isPurchaseType || forceOpen ? 'text' : 'productCombobox', required: threadTypeID !== 1, category: 'basic', priority: 3 },
        ];

        allFields.push(
            { id: 'model', label: 'Model', placeholder: 'Select Model', type: 'modelCombobox', required: false, category: 'details', priority: 4 },
        );

        const bizValueField = { id: 'businessValue', label: 'Business Value', placeholder: 'e.g. 12,500', type: 'businessValue', required: threadTypeID !== 1, category: 'details', priority: 8 };

        // For Inquiry (TID 1): Show Business Value immediately after Model (both Mobile & Desktop)
        if (threadTypeID === 1) {
            allFields.push(bizValueField);
        }

        allFields.push(
            { id: 'quantity', label: 'Quantity', placeholder: 'e.g. 20 no.s', type: 'qtyText', required: false, category: 'details', priority: 6, maxLength: 50 },
            { id: 'dueDT', label: 'Tgt / Close by', placeholder: 'Select date', type: 'date', required: true, category: 'details', priority: 7 },
            { id: 'stageID', label: 'Status', placeholder: 'Select Status', type: 'stageCombobox', required: false, category: 'details', priority: 4.5, isEditHidden: true },
            { id: 'reference', label: `${getThreadNameFromTypeID(threadTypeID, masterData.threadTypes)} Reference`, placeholder: 'e.g. 21', type: 'text', required: false, category: 'details', priority: 5, maxLength: 50 },
            { id: 'sinceDT', label: 'Received on', placeholder: 'Select date', type: 'date', required: false, category: 'details', priority: 6.5 },
        );

        // For Ord Entry (TID != 1): Show Business Value after Reference/SinceDT
        if (threadTypeID !== 1) {
            allFields.push(bizValueField);
        }

        const threadTypeName = getThreadNameFromTypeID(threadTypeID, masterData.threadTypes);
        const notesPlaceholder = threadTypeID === 2
            ? (orderStatusText === 'Completed' || orderStatusText === 'Delivered'
                ? 'e.g. Delivered with 1 yr warranty'
                : 'e.g. Delivery expected by next week')
            : threadTypeID === 3
                ? 'e.g. Issue description and troubleshooting steps'
                : `e.g. Interested in ${masterData.products?.[0]?.name || masterData.products?.[0]?.PNm || 'HP computers'}`;

        allFields.push(
            { id: 'source', label: 'Source', placeholder: 'e.g. Referral', type: 'sourceCombobox', required: false, category: 'details', priority: 6.6 },
            { id: 'pocUserID', label: (threadTypeID == 2) ? 'Credit to' : 'Assigned to', placeholder: 'Select Executive', type: 'userCombobox', required: false, category: 'business', priority: threadTypeID === 2 ? 6.7 : 9 },
            { id: 'nextTaskDT', label: 'Finish by', placeholder: 'Select date', type: 'nextTaskDT', required: false, category: 'business', priority: 10 },
            // { id: 'nextTaskTime', label: 'Next Task Time', placeholder: 'Select time', type: 'time', required: false, category: 'business', priority: 11 },
            { id: 'nextTaskType', label: 'Next Task', placeholder: 'Select Type', type: 'taskTypeCombobox', required: false, category: 'business', priority: 12 },
            { id: 'nextTaskNotes', label: 'Details', placeholder: 'e.g. Arrange meeting', type: 'textarea', required: false, category: 'business', priority: 13, maxLength: 1000 },
        );

        if (threadTypeID === 2) {
            allFields.push(
                { id: 'orderStatus', label: 'Status', type: 'orderStatus', required: true, category: 'details', priority: 13.5 }
            );
        }

        const isCompletedSale = (threadTypeID === 2 || threadTypeID === 5) && (
            Boolean(saveAsCompletedSale) ||
            Boolean(isEditingCompletedSale) ||
            (orderStatusText || '').trim().toLowerCase() === 'completed' ||
            (orderStatusText || '').trim().toLowerCase() === 'completed sale'
        );

        const showFileUpload = Boolean(
            isCompletedSale ||
            (agencyData?.AllowThrFile && threadTypeID !== 2 && threadTypeID !== 5) ||
            Boolean(formData.originalFileName) ||
            Boolean(formData.file)
        );

        allFields.push(
            { id: 'notes', label: 'Notes', placeholder: notesPlaceholder, type: 'textarea', required: false, category: 'additional', priority: 14, maxLength: 4000 },
        );

        if (showFileUpload) {
            allFields.push(
                { id: 'file', label: "Document / Attachment (Up to 1 MB)", type: 'file', required: false, category: 'additional', priority: 14.5 }
            );
        }

        if (hideFields.dueDT) {
            allFields = allFields.filter(field => field.id !== 'dueDT');
        }

        const visibleFields = allFields.filter(field => {
            // MANDATORY FIELDS - Always show these core fields regardless of preferences
            const mandatoryFieldIDs = ['product', 'dueDT', 'businessValue', 'notes', 'orderStatus'];
            if (threadTypeID === 2) {
                mandatoryFieldIDs.push('pocUserID');
            }
            if (field.id === 'file') return true;
            if (mandatoryFieldIDs.includes(field.id)) {
                return true; // These fields are ALWAYS visible
            }

            // Show Quantity only if Product is selected AND PTID is 1 (Product)
            if (field.id === 'quantity') {
                const selectedProductObj = masterData.products.find(p => (p.name || p.PNm) === formData.product);
                return formData.product && ((selectedProductObj?.PTID || selectedProductObj?.offeringType || 1) === 1);
            }
            // Reference field logic - optional for Inquiry types, always visible for others
            if (field.id === 'reference') {
                if (isInquiryType) {
                    return prefsEnabled.reference; // Show based on preference for Inquiry
                }
                return true; // Always show for non-Inquiry types
            }

            // Special handling for connID - hide in edit mode or when preselected
            if (field.id === 'connID') {
                if (threadID || preselectedConnID) return false;
                return true; // Show in create mode when no preselected connection
            }

            // Hide next task fields in edit mode
            if (threadID && ['nextTaskDT', 'nextTaskTime', 'nextTaskType', 'nextTaskNotes'].includes(field.id)) {
                return false;
            }

            // Show next task fields in create mode if preference is enabled and NOT in inquiry only mode
            if (['nextTaskDT', 'nextTaskTime', 'nextTaskType', 'nextTaskNotes'].includes(field.id)) {
                return prefsEnabled.nextTask && !isInqOnlyMode;
            }

            // Handle pocUserID field - Always show Credit to for TID 2; otherwise show if preference enabled
            if (field.id === 'pocUserID') {
                if (threadTypeID === 2) return true;
                if (isSmartSellerMode()) return false;
                const hasMultipleUsers = masterData.users && masterData.users.length > 1;
                return prefsEnabled.pocUserID;
            }

            // Handle sinceDT field
            if (field.id === 'sinceDT') {
                return prefsEnabled.sinceDT || (isInqOnlyMode && (calledFrom === 'ORDDONE' || threadTypeID === 2));
            }

            // Handle source field
            if (field.id === 'source') {
                return prefsEnabled.source;
            }

            // Special handling for contactPerson field
            if (field.id === 'contactPerson') {
                if (!prefsEnabled.contactPerson) return false;

                // In edit mode, check if contact person should actually be visible
                if (threadID) {
                    const selectedConnection = masterData.connections.find(conn => conn.ConnID === formData.connID);
                    if (!selectedConnection) return false;

                    let contacts = selectedConnection.Contacts;
                    if (typeof contacts === 'string') {
                        try {
                            contacts = JSON.parse(contacts);
                        } catch (e) {
                            contacts = [];
                        }
                    }

                    const contactOptions = [];
                    if (selectedConnection.ContactName) {
                        contactOptions.push(selectedConnection.ContactName);
                    }
                    if (contacts && Array.isArray(contacts)) {
                        contacts.forEach(contact => {
                            if (contact.CNm && !contactOptions.includes(contact.CNm)) {
                                contactOptions.push(contact.CNm);
                            }
                        });
                    }

                    // Only show if there are multiple options OR if it has a value and is not the only option
                    return contactOptions.length > 1 || (formData.contactPerson && contactOptions.length === 1);
                }

                return true; // In create mode, show if preference is enabled
            }

            if (field.id === 'model') {
                if (formData.product) {
                    const selectedProduct = masterData.products.find(p => (p.name || p.PNm) === formData.product);
                    if (selectedProduct) {
                        try {
                            const modelsData = selectedProduct.Mdls || selectedProduct.mdls || selectedProduct.Models || selectedProduct.models;
                            if (modelsData) {
                                const models = typeof modelsData === 'string' ? JSON.parse(modelsData) : modelsData;
                                if (Array.isArray(models) && models.length > 0) {
                                    return true;
                                }
                            }
                        } catch (e) {
                        }
                    }
                }
                return prefsEnabled.model;
            }

            // Show stageID (Status) field only for TID 1 with Inquiry or Sales Inquiry thread type
            if (field.id === 'stageID') {
                if (threadID) return false;
                if (threadTypeID === 1) {
                    const threadTypeName = currentThreadType?.SnSng;
                    return threadTypeName === 'Inquiry' || threadTypeName === 'Sales Inquiry';
                }
                return false;
            }

            return prefsEnabled[field.id];
        });

        // Use a grid system: Mobile (2 cols to allow half-width), Desktop (3 cols as requested)
        const gridClass = `grid grid-cols-2 sm:grid-cols-3 gap-4`;

        return (
            <div className={gridClass}>
                {visibleFields.map((field, index, array) => {
                    const getRendered = (id) => {
                        const f = array.find(i => i.id === id);
                        if (!f) return null;
                        return renderFormField(f.id, f.label, f.placeholder, f.type, f.required, f.maxLength, isDisabled);
                    };

                    const isVisible = (id) => array.some(f => f.id === id);

                    // ============================================
                    // MOBILE ONLY LOGIC
                    // ============================================
                    if (isMobileLayout) {
                        // Mobile Inquiry (TID 1) - Special Layout
                        if (threadTypeID === 1) {
                            if (field.id === 'businessValue') {
                                // If Quantity is visible, group Business Value (left) + Quantity (right)
                                if (isVisible('quantity')) {
                                    return (
                                        <div key="biz-qty-group" className="col-span-2 grid grid-cols-2 gap-2">
                                            <div className="w-full">{getRendered('businessValue')}</div>
                                            <div className="w-full">{getRendered('quantity')}</div>
                                        </div>
                                    );
                                }
                                // If Quantity NOT visible, group Business Value (left) + Due Date (right)
                                else if (isVisible('dueDT')) {
                                    return (
                                        <div key="biz-date-group" className="col-span-2 grid grid-cols-2 gap-2">
                                            <div className="w-full">{getRendered('businessValue')}</div>
                                            <div className="w-full">{getRendered('dueDT')}</div>
                                        </div>
                                    );
                                }
                            }

                            // Suppress fields if they were grouped above
                            if (field.id === 'quantity' && isVisible('businessValue')) return null;
                            if (field.id === 'dueDT' && isVisible('businessValue') && !isVisible('quantity')) return null;
                        }
                        // Mobile Orr Entry (TID != 1) - Different Layout
                        else {
                            // Group Business Value (left) + Quantity (right) for Ord Entry
                            if (field.id === 'businessValue' && isVisible('quantity')) {
                                return (
                                    <div key="biz-qty-group" className="col-span-2 grid grid-cols-2 gap-4">
                                        <div className="w-full">{getRendered('businessValue')}</div>
                                        <div className="w-full">{getRendered('quantity')}</div>
                                    </div>
                                );
                            }
                            if (field.id === 'quantity' && isVisible('businessValue')) return null;
                        }
                    }

                    else {
                        // 1. Group Business Value (60%) and Quantity (40%) - DESKTOP ONLY
                        if (field.id === 'businessValue' && isVisible('quantity')) {
                            return (
                                <div key="biz-qty-group" className="col-span-2 sm:col-span-1 flex gap-2">
                                    <div className="w-[60%]">{getRendered('businessValue')}</div>
                                    <div className="w-[40%]">{getRendered('quantity')}</div>
                                </div>
                            );
                        }
                        if (field.id === 'quantity' && isVisible('businessValue')) return null;

                        // Other fields (Reference, SinceDT, DueDT) will render standalone with custom widths below
                    }

                    // 4. Render Standalone Fields
                    const renderedField = renderFormField(field.id, field.label, field.placeholder, field.type, field.required, field.maxLength, isDisabled);

                    // Product always renders, for others verify if rendered
                    if (field.id !== 'product' && !renderedField) return null;

                    // Class Logic
                    // Default: Full width on Mobile (col-span-2), 1 column on Desktop (sm:col-span-1)
                    let className = "col-span-2 sm:col-span-1";

                    // --- Desktop Exceptions ---
                    if (field.id === 'connID') {
                        // Business Name & Order Status: 2 cols on Desktop
                        className = "col-span-2 sm:col-span-2";
                    }
                    if (field.id === 'orderStatus') {
                        className = "col-span-2 sm:col-span-3";
                    }
                    if (field.id === 'notes') {
                        className = isVisible('file') ? "col-span-2 sm:col-span-2" : "col-span-2 sm:col-span-3";
                    }
                    if (field.id === 'file') {
                        className = "col-span-2 sm:col-span-1";
                    }

                    // --- Mobile Exceptions (Half Width) ---
                    // These fields should be side-by-side on mobile (col-span-1)
                    // Note: 'businessValue' is included here so it is half-width when rendered standalone in Mobile Inquiry (not grouped)
                    // or in Mobile Ord Entry (after ref/since).
                    const splitFieldsMobile = ['quantity', 'businessValue', 'dueDT', 'sinceDT', 'reference', 'nextTaskDT', 'nextTaskTime', 'pocUserID'];
                    if (splitFieldsMobile.includes(field.id)) {
                        // Replace 'col-span-2' with 'col-span-1' to allow side-by-side on mobile
                        className = className.replace("col-span-2", "col-span-1");
                    }

                    // Special styling for standalone fields on Desktop
                    let contentClass = "w-full";
                    if (!isMobileLayout) {
                        if (field.id === 'dueDT' || field.id === 'sinceDT') contentClass = "w-[100%]";
                        if (field.id === 'reference') contentClass = "w-[100%]";
                    }

                    return (
                        <div key={field.id} className={`${className} w-full`}>
                            <div className={contentClass}>
                                {/* Double check product field rendering fallback */}
                                {field.id === 'product' ? (renderedField || renderFormField(field.id, field.label, field.placeholder, field.type, field.required, field.maxLength, isDisabled)) : renderedField}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };
    const renderActionButtons = () => {
        // Check if contact person field should be shown and enabled
        const shouldShowContactPerson = (() => {
            if (!prefsEnabled.contactPerson) return false;

            if (threadID) {
                const selectedConnection = masterData.connections.find(conn => conn.ConnID === formData.connID);
                if (!selectedConnection) return false;

                let contacts = selectedConnection.Contacts;
                if (typeof contacts === 'string') {
                    try {
                        contacts = JSON.parse(contacts);
                    } catch (e) {
                        contacts = [];
                    }
                }

                const contactOptions = [];
                if (selectedConnection.ContactName) {
                    contactOptions.push(selectedConnection.ContactName);
                }
                if (contacts && Array.isArray(contacts)) {
                    contacts.forEach(contact => {
                        if (contact.CNm && !contactOptions.includes(contact.CNm)) {
                            contactOptions.push(contact.CNm);
                        }
                    });
                }

                return contactOptions.length > 1 || (formData.contactPerson && contactOptions.length === 1);
            }

            return !preselectedConnID; // In create mode, show if no preselected connection
        })();
        // Check if status is required and not selected (e.g., in sale entry TID 2)
        const isStatusMissing = threadTypeID === 2 && !orderStatusText?.trim();
        const isSaveDisabled = isDisabled || isStatusMissing;

        return (
            <div className=" sm:px-0 sm:sticky sm:bottom-0 sm:bg-background sm:pb-3 ">
                <div className="flex flex-col gap-3 sm:flex-row sm:justify-start sm:gap-3 w-full">
                    {/* Mobile Layout - More Details half-width on left, Save/Update full-width */}
                    <div className="flex sm:hidden flex-col gap-3 w-full">
                        {!hideFields.moreDtls &&
                            <div className="flex justify-start">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setDialogStates(prev => ({
                                            ...prev,
                                            fields: true,
                                            // main: false,
                                            threadTypeID: threadTypeID
                                        }));
                                    }}
                                    className="sahi-secondary-button px-2"
                                    disabled={isDisabled}
                                >
                                    <Plus className="h-4 w-4  " />
                                    More Details
                                </Button>
                            </div>
                        }
                        <Button
                            onClick={handleSaveThread}
                            className="sahi-primary-button w-full"
                            disabled={isSaveDisabled}
                        >
                            <Check className="h-4 w-4  " />
                            {threadID ? 'Update' : 'Save'} {getThreadNameFromTypeID(threadTypeID, masterData.threadTypes)}
                        </Button>
                    </div>
                    {/* Desktop Layout - Buttons side by side */}
                    <div className="hidden sm:flex sm:gap-3">
                        <Button
                            onClick={handleSaveThread}
                            className="sahi-primary-button flex-1 sm:flex-none sm:min-w-[140px]"
                            disabled={isSaveDisabled}
                        >
                            <Check className="h-4 w-4  " />
                            {threadID ? 'Update' : 'Save'} {getThreadNameFromTypeID(threadTypeID, masterData.threadTypes)}
                        </Button>
                        {!hideFields.moreDtls && <Button
                            variant="outline"
                            onClick={() => {
                                setDialogStates(prev => ({
                                    ...prev,
                                    fields: true,
                                    // main: false,
                                    threadTypeID: threadTypeID
                                }));
                            }}
                            className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[140px]"
                            disabled={isDisabled}
                        >
                            <Plus className="h-4 w-4  " />
                            More Details
                        </Button>}
                        {!forceOpen && (
                            <Button
                                variant="outline"
                                onClick={() => setIsOpen(false)}
                                className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]"
                            >
                                <X className="h-4 w-4  " />
                                Close
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        );
    };
    // Render dialog header with title and actions
    const renderHeader = () => {
        const getHeaderTitle = () => {
            const baseTitle = threadID ? 'Edit ' : '';
            const threadTypeName = getThreadNameFromTypeID(threadTypeID, masterData.threadTypes);
            const suffix = threadID ? '' : ' Entry';

            if ((threadID || fromConnectionDetail || preselectedConnID) && formData.connID) {
                const selectedConnection = masterData.connections.find(conn => conn.ConnID === formData.connID);
                const businessName = selectedConnection?.BizName || selectedConnection?.ContactName || 'Loading...';
                const fullTitle = calledFrom == 'THRDONE' ? `Conver to Sale - ${businessName}` : `${baseTitle}${threadTypeName}${suffix} - ${businessName}`;

                if (isMobileLayout) {
                    const mobileHeader = calledFrom === 'THRDONE' ? 'Convert to Sale' : `${baseTitle}${threadTypeName}${suffix}`;
                    return getMobileHeaderTitle(mobileHeader); // Adjust max length as needed
                }
                return fullTitle;
            }

            return calledFrom == 'THRDONE' ? `Convert to Sale` : `${baseTitle}${threadTypeName}${suffix}`;
        };
        return (
            <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
                <div className="sm:hidden flex items-center justify-between w-full h-8">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                            <ArrowLeft className={threads_Classes.iconSizeLg} />
                            <span className="sr-only">Back</span>
                        </DialogClose>
                        <DialogTitle className="text-xl text-color flex gap-2">
                            <div className="truncate">
                                {getHeaderTitle()}
                            </div>
                            {threadID && getThreadNameFromTypeID(threadTypeID, masterData.threadTypes) === "Sale" && (<button
                                onClick={() => setDialogStates(prev => ({ ...prev, connectionDetails: true, createdConnID: formData.connID }))}
                                className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                            >
                                <SquareArrowOutUpRight className="h-5 w-5 text-sahi-green" />
                            </button>)}
                        </DialogTitle>
                    </div>
                    {/* Right Side */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {(threadID && canShowDltBtn) && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 p-0 hover:bg-white/10 hover:text-white"
                                onClick={() => setDialogStates(prev => ({ ...prev, deleteAlert: true }))}
                            >
                                <Trash2 className={threads_Classes.iconSize} />
                            </Button>
                        )}
                    </div>
                </div>
                <DialogTitle className="hidden sm:block text-lg md:text-xl">
                    <div className="flex items-center justify-start gap-2">
                        {getHeaderTitle()}
                        {threadID && getThreadNameFromTypeID(threadTypeID, masterData.threadTypes) === "Sale" && (<button
                            onClick={() => setDialogStates(prev => ({ ...prev, connectionDetails: true, createdConnID: formData.connID }))}
                            className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                        >
                            <SquareArrowOutUpRight className="h-5 w-5" />
                        </button>)}
                    </div>
                </DialogTitle>
                <div className="hidden sm:flex items-center gap-2">
                    {(threadID && canShowDltBtn) && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 p-0 sm:text-text-color sm:hover:bg-gray-200"
                            onClick={() => setDialogStates(prev => ({ ...prev, deleteAlert: true }))}
                        >
                            <Trash2 className={threads_Classes.iconSize} />
                        </Button>
                    )}
                    <DialogClose className="flex-shrink-0" tabIndex={-1}>
                        <X className={threads_Classes.iconSizeMd} />
                        <span className="sr-only">Close</span>
                    </DialogClose>
                </div>
            </DialogHeader>
        );
    };


    const renderDisableConn = () => {
        const isShow = isMobileLayout && formData.connID && (threadID || preselectedConnID);
        const selectedConnection = masterData.connections.find(conn => conn.ConnID === formData.connID);
        const businessName = selectedConnection?.BizName || selectedConnection?.ContactName || 'Loading...';

        return isShow ? (<>
            <div key='connID' className={threads_Classes.spacing}>
                <Label htmlFor='connID' required={true}>Contact</Label>
                <Input
                    id='connID'
                    name='connID'
                    value={businessName}
                    onChange={handleInputChange}
                    placeholder={'Contact'}
                    className={threads_Classes.input}
                    type={'text'}
                    autoComplete={'off'}
                    maxLength={50}
                    disabled={true}
                />
            </div>
        </>) : <></>
    }

    // Determine if redirection is happening to prevent main dialog flash
    const isRedirecting = isOpen && !hasConnections && !threadID && !preselectedConnID && !fromConnectionDetail;

    return (
        <>
            {triggerButton && (
                <Button onClick={() => setIsOpen(true)} className={triggerClassName}>
                    <Plus className={`  ${threads_Classes.iconSize}`} />
                    {triggerText}
                </Button>
            )}

            {/* Main Thread Entry Dialog */}
            <Dialog
                open={isOpen && !dialogStates.masterDialog && !isRedirecting}
                hidden={isOpen && dialogStates.masterDialog}
                onOpenChange={setIsOpen}
                preventOutsideClick={true}
                forceOpen={forceOpen}
            >
                <DialogContent
                    className={`flex flex-col sm:w-auto sm:max-w-[95vw] ${isInqOnlyMode
                        ? `lg:max-w-[950px] ${isSmartSellerMode() ? 'min-[798px]:min-w-[650px]' : 'min-[798px]:min-w-[850px]'}`
                        : 'lg:max-w-[1250px] min-[998px]:min-w-[998px]'
                        } sm:min-h-0 sm:max-h-[95vh] sm:h-auto sm:rounded-lg rounded-none min-[700px]:min-w-[700px] ${isAutoOpeningContact ? 'opacity-0' : 'opacity-100'
                        }`}
                >
                    {renderHeader()}
                    <div className="pb-4 px-4 flex-1 scrollbar-no-arrows overflow-y-scroll sm:max-h-[85vh] sm:pb-0">
                        {uiStates.isLoadingThread ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="text-sm text-gray-500">Loading thread data...</div>
                            </div>
                        ) : (
                            <>
                                <div className={`space-y-4 pb-4`}>
                                    {renderDisableConn()}
                                    {renderDynamicFields()}
                                    {renderExtraFields()}



                                    {/* File Upload Section - Only for Premium Users */}
                                    {(masterData?.allowSecConns && secondaryContacts.length > 0) && (
                                        <div className={`grid ${threads_Classes.gridCols1} ${threads_Classes.gap4}`}>
                                            <div className={threads_Classes.spacing}>
                                                <Label>
                                                    Secondary Contacts
                                                </Label>
                                                <MultiSelect
                                                    variant="default"
                                                    options={secondaryContacts}
                                                    onValueChange={(values) => setFormData(prev => ({
                                                        ...prev,
                                                        secConnIDs: values
                                                    }))}
                                                    defaultValue={formData?.secConnIDs || ''}
                                                    placeholder={'Select Contacts'}
                                                    label={'contacts'}
                                                    outsideLabel={true}
                                                    selectWidth={isMobileLayout ? '100%' : isInqOnlyMode ? '50%' : '32%'}
                                                    animation={2}
                                                    maxCount={50}
                                                    isSelectAll={false}
                                                    isSearch={true}
                                                    closeIcon={false}
                                                    emptyMessage={'No contacts found.'}
                                                    disabled={isDisabled}
                                                />
                                            </div>
                                        </div>
                                    )}



                                    {/* Share by Email Section */}
                                    {[2, 3].includes(threadTypeID) && !hideFields.sharingOpts && <SharingOptions
                                        connID={formData.connID}
                                        isEmailShared={formData.shareByEmail}
                                        onEmailShareChange={handleEmailShareChange}
                                        showInAddMode={true}
                                        isEditMode={!!threadID}
                                        onSettingsClick={handleSettingsClick}
                                        showWhatsAppOption={true}
                                        isWhatsAppShared={shareByWhatsApp}
                                        onWhatsAppShareChange={(checked) => setShareByWhatsApp(checked)}
                                        showCompletedSaleOption={false}
                                        isCompletedSale={saveAsCompletedSale}
                                        onCompletedSaleChange={(checked) => setSaveAsCompletedSale(checked)}
                                        className="space-y-3"
                                    />}
                                </div>
                                {renderActionButtons()}
                            </>
                        )}
                    </div>

                    {/* Email configure dialog box */}
                    <Policies open={emailConfigOpen} onOpenChange={setEmailConfigOpen} />
                </DialogContent>
            </Dialog>

            <ConnectionFieldsDialog
                open={dialogStates.fields}
                onOpenChange={(open) => {
                    setDialogStates(prev => ({ ...prev, fields: open }));
                    if (!open) setIsOpen(true);
                }}
                onSave={(fields) => {
                    setUIStates(prev => ({ ...prev, selectedFields: fields }));
                    setDialogStates(prev => ({ ...prev, fields: false }));
                    setIsOpen(true);
                }}
                connID={formData.connID}
                initialSelectedFields={uiStates.selectedFields}
                threadTypeID={dialogStates.threadTypeID || threadTypeID}
                isEditingContact={false}
                isInquiryChecked={true}
                hideTitles={true}
                calledFrom="THREAD_ENTRY"
            />

            {/* Connection Entry Dialog */}
            <ConnectionEntryDialog
                triggerButton={false}
                open={dialogStates.connection}
                onOpenChange={(open) => {
                    if (!open && !canCloseSubModal()) {
                        return;
                    }
                    setDialogStates(prev => ({ ...prev, connection: open }));
                    if (!open && hasConnections) {
                        setUIStates(prev => ({ ...prev, connectionSearchText: '' }));
                        setIsOpen(true);
                    }
                }}
                parentDialogSetter={setIsOpen}
                onConnectionAdded={handleConnectionAdded}
                prefilledData={uiStates.connectionSearchText ? { businessName: uiStates.connectionSearchText } : null}
                // Only pass FORCE_INQUIRY if NOT coming from the dropdown "New Contact" option
                calledFrom={isMobileLayout ? 'FORCE_INQUIRY' : ''}
                forceOpen={isMobileLayout ? forceOpen : false}
                threadTypeID={threadTypeID}
                showEnterInquiry={isMobileLayout}
            // isBasic={isMobileLayout}
            />

            {dialogStates.masterDialog && (
                <MastersDialog
                    open={dialogStates.masterDialog}
                    onClose={handleMasterDialogClose}
                    masterType={masterDialogType}
                    directAdd={true}
                    openEntry={true}
                    prefilledName={uiStates.masterSearchText}
                    parentDialogOpen={isOpen}
                    onProductAdded={(itemName) => {
                        // Handle when ANY item is added from master dialog (not just products)
                        if (selectedField) {
                            // Set the newly added item to the corresponding field
                            setFormData(prev => ({
                                ...prev,
                                [selectedField]: itemName
                            }));

                            // Show success message
                            // toast.success(`${itemName} added.`);
                        }

                        // Close the master dialog and reset states
                        handleMasterDialogClose();
                    }}
                />
            )}
            <AlertBox
                open={dialogStates.deleteAlert}
                onOpenChange={(open) => setDialogStates(prev => ({ ...prev, deleteAlert: open }))}
                title="Please Confirm"
                description={`Are you sure you wish to delete this ${currThreadType?.SnSng.toLowerCase()}?`}
                actions={[
                    {
                        label: "Cancel",
                        variant: "cancel",
                        value: "cancel",
                        className: "w-full sm:w-auto",
                        icon: <X className="h-4 w-4" />
                    },
                    {
                        label: `Delete ${currThreadType?.SnSng}`,
                        variant: "action",
                        value: "delete",
                        className: "w-full sm:w-auto",
                        icon: <Trash2 className="h-4 w-4" />
                    }
                ]}
                onAction={(actionValue) => {
                    if (actionValue === "delete") {
                        handleDeleteThread(extOperation);
                    }
                }}
            />

            {/* Business Prospect Confirmation Dialog */}
            <AlertDialog
                open={dialogStates.bizProspectConfirm}
                onOpenChange={(open) => setDialogStates(prev => ({ ...prev, bizProspectConfirm: open }))}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            Confirm Zero Business Value
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you wish to keep zero value of this business prospect?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="!justify-start gap-3">
                        <Button
                            onClick={handleBizProspectConfirm}
                            className="sahi-primary-button flex-1 sm:flex-none sm:min-w-[120px]"
                        >
                            <Check className="w-3 h-3" />
                            Yes, Continue
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handleBizProspectCancel}
                            className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]"
                        >
                            <X className="w-3 h-3" />
                            No, Cancel
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {dialogStates.threadDetails && <ThrDetails
                isOpen={dialogStates.threadDetails}
                onClose={(open) => {
                    setDialogStates((prev) => ({ ...prev, threadDetails: open }))
                    setIsOpen(false)
                }}
                threadID={selectedThreadID}
                initialData={selectedThreadForDetails}
            />}

            <Dialog
                open={dialogStates.modelDialog}
                onOpenChange={(open) => setDialogStates(prev => ({ ...prev, modelDialog: open }))}
            >
                <DialogContent className="sm:max-w-md w-full">
                    {(() => {
                        const selectedProductForLabel = masterData.products.find(p => (p.name || p.PNm) === formData.product);
                        const offeringTypeLabel = (selectedProductForLabel?.PTID || selectedProductForLabel?.offeringType || 1) === 1 ? 'Model' : 'Package';

                        return (
                            <>
                                <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
                                    <div className="sm:hidden flex items-center justify-between w-full h-8">
                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                            <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                                                <ArrowLeft className="h-6 w-6" />
                                                <span className="sr-only">Back</span>
                                            </DialogClose>
                                            <DialogTitle className="text-lg font-medium">
                                                <div className="truncate">{offeringTypeLabel} Entry</div>
                                            </DialogTitle>
                                        </div>
                                    </div>
                                    <DialogTitle className="hidden sm:block text-lg md:text-xl">
                                        {offeringTypeLabel} Entry
                                    </DialogTitle>
                                    <div className="hidden sm:flex items-center gap-2">
                                        <DialogClose className="flex-shrink-0" tabIndex={-1}>
                                            <X className="h-5 w-5" />
                                            <span className="sr-only">Close</span>
                                        </DialogClose>
                                    </div>
                                </DialogHeader>
                                <div className="px-4 pb-4 space-y-4">
                                    <div className="space-y-2">
                                        <Label>{offeringTypeLabel} Name</Label>
                                        <Input
                                            value={dialogStates.modelSearchText}
                                            onChange={(e) => setDialogStates(prev => ({ ...prev, modelSearchText: e.target.value }))}
                                            placeholder={`e.g. ${offeringTypeLabel === 'Model' ? 'Gold' : 'Basic'}`}
                                            autoFocus
                                            className="h-11"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleModelAdd();
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <Button
                                            onClick={handleModelAdd}
                                            className="sahi-primary-button flex-1 sm:flex-none sm:min-w-[140px]"
                                        >
                                            <Check className="h-4 w-4" strokeWidth={3} />
                                            Save {offeringTypeLabel}
                                        </Button>
                                        {!isMobileLayout && (
                                            <Button
                                                variant="outline"
                                                onClick={() => setDialogStates(prev => ({ ...prev, modelDialog: false }))}
                                                className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[100px]"
                                            >
                                                <X className="w-3 h-3" />
                                                Close
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </>
                        );
                    })()}
                </DialogContent>
            </Dialog>

            {/* Connection Details Dialog */}
            {(dialogStates.connectionDetails && dialogStates.createdConnID) && <ConnectionDetailsWrapper
                isOpen={dialogStates.connectionDetails}
                onClose={() => {
                    setDialogStates(prev => ({ ...prev, connectionDetails: false, createdConnID: null }));
                    if (!isMobileLayout) {
                        setIsOpen(true);
                    }
                }}
                connID={dialogStates.createdConnID}
            />}

            {/* Order Stages Dialog for TID 2 */}
            <OrderStagesDialog
                open={orderStagesDialogOpen}
                onClose={() => setOrderStagesDialogOpen(false)}
                onOpenChange={setOrderStagesDialogOpen}
                initialView={orderStagesInitialView}
            />
        </>
    );
};

export default ThreadEntry;